<template>
  <div
    ref="rootRef"
    class="scene-canvas"
    :class="{ 'model-drag-over': isModelDragOver }"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
    @contextmenu.prevent="onCanvasContextMenu"
  >
    <input
      ref="realisticFileInputRef"
      type="file"
      class="realistic-hidden-input"
      accept="image/png,image/jpeg,image/jpg,image/webp"
      @change="onRealisticFileChange"
    />

    <!-- ===== 场景层视口：唯一场景内容容器（3D 画布 / HTML 大屏 / 信息卡 / 罗盘）。
         contain: layout paint 让 position:fixed 后代（AI 大屏内容）被锁进本容器；系统层绝不可加。 ===== -->
    <div ref="containerRef" class="scene-viewport">
      <img
        v-if="realisticMode && realisticBgUrl"
        class="realistic-bg-image"
        :src="realisticBgUrl"
        :style="{ opacity: bgOpacity }"
        alt="实景背景"
      />
      <div v-if="showCompass" class="compass-indicator" aria-label="方位指示器">
        <div class="compass-ring" :style="{ transform: `rotate(${compassRotationDeg}deg)` }">
          <span class="dir north">N</span>
          <span class="dir east">E</span>
          <span class="dir south">S</span>
          <span class="dir west">W</span>
        </div>
      </div>

      <DashboardOverlay
        v-if="showPreviewDashboardOverlay && activeSceneData"
        :config="dashboardConfigForCompose"
        :scene="activeSceneData"
        :slice="previewDashboardSlice"
        :live-data="editorPreviewMergedLiveData"
        :selected-object-id="selectedId"
        :can-execute-controls="false"
        :horizontal-fit-widgets="false"
        @focus-object="onPreviewDashboardFocus"
      />

      <!-- HTML 大屏（场景层：整屏覆盖，z-index var(--z-scene-overlay)，压住罗盘） -->
      <HtmlOverlayHost
        v-if="htmlOverlayProp && !dashboardComposeMode && activeSceneData"
        :overlay="htmlOverlayProp"
        :slice="htmlOverlaySliceProp ?? previewDashboardSlice"
        :live-data="editorPreviewMergedLiveData"
        :scene="activeSceneData"
        :selected-object-id="selectedId"
        :overlay-layer-el="viewportOverlayLayerRef"
        @focus-object="onPreviewDashboardFocus"
        @close="emit('html-overlay-close')"
      />

      <div v-if="simulationMonitorMode" class="simulation-viz-placeholder" role="note">
        仿真可视化占位（二期：热力图 / 流场矢量）
      </div>
    </div>

    <!-- ===== 场景浮层：临时信息卡容器（viewport 的兄弟，仍低于系统层，z-index 兜底） ===== -->
    <div ref="viewportOverlayLayerRef" class="viewport-overlay-layer" />

    <!-- ===== 系统层：编辑器 UI（viewport 的兄弟节点，结构上天然在场景之上；z-index 兜底） ===== -->

    <!-- 气泡菜单 -->
    <BubbleMenu
      v-if="!readonlyMode && selectedId && bubblePos"
      :id="selectedId"
      :pos="bubblePos"
      :multi-select-ids="selectedIds"
      :on-history-push="() => histManager.push(sceneStore.getEditorSnapshot())"
      @close="deselect"
      @open-array="arrayDialogVisible = true"
    />

    <!-- 连线属性气泡（颜色 / 标注） -->
    <div
      v-if="!readonlyMode && selectedConnectionId && connectionBubblePos"
      class="connection-bubble"
      :style="{ left: connectionBubblePos.x + 'px', top: connectionBubblePos.y + 'px' }"
      @click.stop
    >
      <div class="connection-bubble-title">连线</div>
      <label class="connection-bubble-row">
        <span>颜色</span>
        <input type="color" :value="activeConnectionColor" @input="onConnectionColorInput" />
      </label>
      <label class="connection-bubble-row">
        <span>标注</span>
        <input v-model="connectionLabelDraft" type="text" class="connection-bubble-input" placeholder="如：物料流向" @change="commitConnectionLabel" />
      </label>
      <button type="button" class="connection-bubble-del" @click="deleteSelectedConnection">删除连线</button>
    </div>

    <!-- 右键菜单 -->
    <div
      v-if="!readonlyMode && contextMenu.visible"
      ref="contextMenuRef"
      class="context-menu"
      :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
      @click.stop
    >
      <template v-if="contextMenu.objectId">
        <div class="ctx-item ctx-item-row" @click="ctxCopy">
          <span class="ctx-item-row-label"><Copy :size="14" class="ctx-icon" /> 复制</span>
          <kbd class="ctx-kbd ctx-kbd-combo" title="Ctrl + C">Ctrl+C</kbd>
        </div>
        <div class="ctx-item ctx-item-row" :class="{ disabled: !canPaste }" @click="ctxPaste">
          <span class="ctx-item-row-label"><ClipboardPaste :size="14" class="ctx-icon" /> 粘贴到此处</span>
          <kbd class="ctx-kbd ctx-kbd-combo" title="Ctrl + V">Ctrl+V</kbd>
        </div>
        <div class="ctx-item ctx-item-row" @click="ctxDuplicate">
          <span class="ctx-item-row-label"><Clipboard :size="14" class="ctx-icon" /> 复制到此处</span>
          <kbd class="ctx-kbd ctx-kbd-combo" title="Ctrl + D">Ctrl+D</kbd>
        </div>
        <div class="ctx-item" @click="openArrayFromContext"><Grid2x2 :size="14" class="ctx-icon" /> 阵列…</div>
        <div class="ctx-item" @click="ctxRename"><Pencil :size="14" class="ctx-icon" /> 重命名</div>
        <div
          v-if="selectedPathId && contextMenu.objectId"
          class="ctx-item"
          @click="ctxAssignPathToObject"
        >
          <span class="ctx-emoji" aria-hidden="true">🛤</span> 将当前路径分配给该对象
        </div>
        <div
          v-if="contextMenu.objectId && objectHasPath(contextMenu.objectId)"
          class="ctx-item"
          @click="ctxRemoveObjectPath"
        >
          移除路径
        </div>
        <div class="ctx-separator" />
        <div class="ctx-item ctx-item-row" @click="ctxToggleObjectLock">
          <span class="ctx-item-row-label">
            <Lock v-if="!contextMenuTargetLocked" :size="14" class="ctx-icon" />
            <Unlock v-else :size="14" class="ctx-icon" />
            {{ contextMenuTargetLocked ? '解锁编辑' : '锁定编辑' }}
          </span>
        </div>
        <div
          class="ctx-item"
          :class="{ disabled: !contextMenuCanGroup }"
          @click="ctxGroupFromMenu"
        >
          <Group :size="14" class="ctx-icon" /> 成组
        </div>
        <div
          class="ctx-item"
          :class="{ disabled: !contextMenuTargetHasGroup }"
          @click="ctxUngroupFromMenu"
        >
          <Ungroup :size="14" class="ctx-icon" /> 取消组合
        </div>
        <div class="ctx-separator" />
        <div class="ctx-item" @click="startConnect"><span class="ctx-emoji" aria-hidden="true">🔗</span> 连接到...</div>
        <div class="ctx-separator" />
        <div class="ctx-item danger ctx-item-row" @click="deleteSelected">
          <span class="ctx-item-row-label"><Trash2 :size="14" class="ctx-icon" /> 删除</span>
          <kbd class="ctx-kbd" title="Delete / Backspace">Del</kbd>
        </div>
      </template>
      <template v-else>
        <div class="ctx-item ctx-item-row" :class="{ disabled: !canPaste }" @click="ctxPaste">
          <span class="ctx-item-row-label"><ClipboardPaste :size="14" class="ctx-icon" /> 粘贴到此处</span>
          <kbd class="ctx-kbd ctx-kbd-combo" title="Ctrl + V">Ctrl+V</kbd>
        </div>
        <div class="ctx-item ctx-item-row" @click="ctxSelectAll">
          <span class="ctx-item-row-label"><Check :size="14" class="ctx-icon" /> 全选</span>
          <kbd class="ctx-kbd ctx-kbd-combo" title="Ctrl + A">Ctrl+A</kbd>
        </div>
      </template>
    </div>

    <!-- 连线预览提示 -->
    <div v-if="!readonlyMode && connectingFromId" class="connect-hint">
      虚线跟随鼠标，点击目标对象完成连线（仅同楼层）· Esc 取消
    </div>
    <div v-if="floorEditBlocked" class="floor-edit-lock-hint">
      已开启独编楼层：当前楼层仅浏览不可编辑
    </div>
    <div
      v-if="debugRealtimeEnabled && runtimeBadgeVisible && readonlyMode"
      class="runtime-connection"
      :class="[`state-${runtimeConnectionState}`]"
    >
      实时连接：{{ runtimeConnectionLabel }}<span v-if="runtimeIsStale">（stale）</span>
      <span v-if="lastSnapshotAtText"> · {{ lastSnapshotAtText }}</span>
    </div>
    <div v-if="!readonlyMode && transformMode === 'rotate'" class="connect-hint rotate-hint">
      旋转模式：拖拽选中对象进行旋转，Shift 吸附 15°，按 R 或 Esc 退出
    </div>
    <div v-if="!readonlyMode && rotateDragging" class="connect-hint rotate-angle-hint">
      当前角度：{{ rotateDisplayDeg }}°
    </div>
    <div v-if="!readonlyMode && measureToolActive" class="connect-hint measure-hint">
      <template v-if="measureHasDraft">点击第二个点完成测量 · Esc 取消当前</template>
      <template v-else>点击地面第一个点开始测量 · Esc 退出</template>
    </div>
    <div v-if="!readonlyMode && pathToolActive" class="connect-hint measure-hint">
      <template v-if="pathHasDraft">路径工具：继续点击追加点 · 闭合时点击起点 · Shift 正交 · 双击或 Enter 完成 · Esc 取消</template>
      <template v-else>路径工具：点击地面开始绘制路径</template>
    </div>
    <div v-if="!readonlyMode && (editorTool === 'roadStroke' || editorTool === 'wallStroke' || editorTool === 'riverStroke' || editorTool === 'pipeStroke')" class="connect-hint measure-hint">
      <template v-if="layoutHasDraft">
        {{
          editorTool === 'roadStroke'
            ? '折线道路'
            : editorTool === 'wallStroke'
              ? '折线围墙'
              : editorTool === 'riverStroke'
                ? '河流'
                : '折线管线'
        }}：继续点击 · 点击起点闭合 · Shift 正交 · 双击或 Enter 完成 · Esc 取消
      </template>
      <template v-else>
        {{
          editorTool === 'roadStroke'
            ? '折线道路'
            : editorTool === 'wallStroke'
              ? '折线围墙'
              : editorTool === 'riverStroke'
                ? '河流'
                : '折线管线'
        }}：点击地面开始笔画
      </template>
    </div>
    <div v-if="!readonlyMode && editorTool === 'pipeConnect'" class="connect-hint measure-hint">
      <template v-if="pipeConnectHasDraft">管线连接：请选择终点端口 · Esc 取消</template>
      <template v-else>管线连接：点击组件端口作为起点，再点终点端口完成</template>
    </div>

    <!-- 精确输入浮层 -->
    <div
      v-if="!readonlyMode && preciseInput.visible"
      class="precise-input-overlay"
      :style="{ left: preciseInput.x + 'px', top: preciseInput.y + 'px' }"
    >
      <label class="precise-label">{{ preciseInput.label }}</label>
      <input
        ref="preciseInputRef"
        v-model.number="preciseInput.value"
        type="number"
        class="precise-input"
        @keyup.enter="confirmPreciseInput"
        @keyup.escape="preciseInput.visible = false"
      />
      <span class="precise-unit">米</span>
      <button class="precise-confirm" @click="confirmPreciseInput" aria-label="确认"><Check :size="16" /></button>
    </div>

    <!-- 右侧面板：普通模式「场景 | 仿真资源」；组态模式追加「控件」；运行数据观测在独立页由顶栏抽屉打开，不占右侧
         rightPanelCollapsed：SceneTabs 那一条上可以把它完全收起，不占画布空间；Transition 给收起/展开加一点滑出动画 -->
    <Transition name="right-panel-collapse">
    <div
      v-show="showSceneRightPanels && !rightPanelCollapsed"
      class="right-panels"
      :class="{ 'right-panels--compose': dashboardComposeMode }"
    >
      <template v-if="dashboardComposeMode">
        <div class="compose-right-tabs">
          <!-- 场景/仿真资源/控件切换按钮已上移到 SceneTabs 那一条，节省一整行高度给组态画布 -->
          <div class="compose-right-tab-body">
            <div v-show="composeRightPanelTab === 'scene'" class="compose-tab-pane compose-tab-pane--scene">
              <div class="compose-scene-stack">
                <ObjectListPanel
                  :on-history-push="() => histManager.push(sceneStore.getEditorSnapshot())"
                  @toggle-hidden="onObjectListToggleHidden"
                />
                <PropertiesPanel
                  :object-id="selectedId"
                  :object-ids="selectedIds"
                  :selected-path-id="selectedPathId"
                  :on-history-push="() => histManager.push(sceneStore.getEditorSnapshot())"
                  @open-connect-data="emit('open-connect-data')"
                  @open-project-connect="emit('open-project-connect')"
                />
              </div>
            </div>
            <div v-show="composeRightPanelTab === 'physics'" class="compose-tab-pane compose-tab-pane--physics">
              <div class="scene-right-stack">
                <PhysicsNavPanel :on-history-push="() => histManager.push(sceneStore.getEditorSnapshot())" />
                <PhysicsDetailPanel :on-history-push="() => histManager.push(sceneStore.getEditorSnapshot())" />
              </div>
            </div>
            <div v-show="composeRightPanelTab === 'widget'" class="compose-tab-pane compose-tab-pane--widget">
              <div class="compose-widget-panel-inner">
                <ComposeWidgetList
                  :widgets="dashboardConfigForCompose.widgets"
                  :selected-id="composeSelectedWidgetId"
                  @select="onComposeWidgetListSelect"
                />
                <WidgetInspector
                  layout="side"
                  :widget="selectedComposeWidget"
                  :scene="activeSceneData"
                  :grid-size="composeGridSize"
                  :position-clamp-min-frac="composePositionClampMinFrac"
                  @update-widget="onComposeWidgetUpdate"
                  @delete-widget="onComposeWidgetDelete"
                  @reorder-widget="onComposeWidgetReorder"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="compose-right-tabs">
          <!-- 场景/仿真资源切换按钮已上移到 SceneTabs 那一条，节省一整行高度给画布 -->
          <div class="compose-right-tab-body">
            <div v-show="nonComposeRightPanelTab === 'scene'" class="compose-tab-pane compose-tab-pane--scene">
              <div class="scene-right-stack">
                <ObjectListPanel
                  :on-history-push="() => histManager.push(sceneStore.getEditorSnapshot())"
                  @toggle-hidden="onObjectListToggleHidden"
                />
                <PropertiesPanel
                  :object-id="selectedId"
                  :object-ids="selectedIds"
                  :selected-path-id="selectedPathId"
                  :on-history-push="() => histManager.push(sceneStore.getEditorSnapshot())"
                  @open-connect-data="emit('open-connect-data')"
                  @open-project-connect="emit('open-project-connect')"
                />
              </div>
            </div>
            <div v-show="nonComposeRightPanelTab === 'physics'" class="compose-tab-pane compose-tab-pane--physics">
              <div class="scene-right-stack">
                <PhysicsNavPanel :on-history-push="() => histManager.push(sceneStore.getEditorSnapshot())" />
                <PhysicsDetailPanel :on-history-push="() => histManager.push(sceneStore.getEditorSnapshot())" />
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    </Transition>

    <!-- 小地图（置于右侧面板之后，保证叠在上层、交互不被挡） -->
    <Minimap
      v-if="!readonlyMode && sm && showMinimap"
      ref="minimapRef"
      :scene-manager="sm"
      @navigate="onMinimapNavigate"
    />
    <div v-if="realisticMode && !readonlyMode" class="realistic-overlay-panel" @click.stop>
      <div class="realistic-title">实景叠加</div>
      <button type="button" class="realistic-btn" @click="openRealisticUpload">
        {{ realisticBgUrl ? '更换实景照片' : '上传实景照片' }}
      </button>
      <template v-if="realisticBgUrl">
        <label class="realistic-row">
          <span>照片不透明度</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            :value="bgOpacity"
            @input="onBgOpacityInput(($event.target as HTMLInputElement).value)"
          />
          <strong>{{ Math.round(bgOpacity * 100) }}%</strong>
        </label>
        <label class="realistic-row">
          <span>相机 FOV</span>
          <input
            type="range"
            min="30"
            max="90"
            step="1"
            :value="fovValue"
            @input="onFovInput(($event.target as HTMLInputElement).value)"
          />
          <strong>{{ fovValue }}°</strong>
        </label>
        <button type="button" class="realistic-btn primary" @click="exportComposite">
          导出合成图
        </button>
      </template>
      <button type="button" class="realistic-btn danger" @click="exitRealisticMode">
        退出实景模式
      </button>
    </div>

    <!-- 模型导入弹窗 -->
    <ImportModelDialog
      :visible="!readonlyMode && showImportDialog"
      :initial-files="importInitialFiles"
      @close="showImportDialog = false; importInitialFiles = []"
    />

    <ArrayDuplicateDialog
      :visible="!readonlyMode && arrayDialogVisible"
      @close="arrayDialogVisible = false"
      @confirm="onArrayDuplicateConfirm"
    />

    <!-- 模型文件拖入高亮提示 -->
    <div v-if="!readonlyMode && isModelDragOver" class="model-drop-hint">
      松开以导入 3D 模型
    </div>

    <!-- 框选矩形 -->
    <div
      v-if="!readonlyMode && selectionBox"
      class="selection-box"
      :style="{
        left: selectionBox.left + 'px',
        top: selectionBox.top + 'px',
        width: selectionBox.width + 'px',
        height: selectionBox.height + 'px'
      }"
    />

    <!-- 开发模式测试辅助面板 -->
    <div v-if="isDev" class="dev-test-panel" aria-label="开发测试面板">
      <button class="dev-btn" @click="devSelectFirst" aria-label="选中第一个">选中第一个</button>
      <button class="dev-btn" @click="devMoveSelected" aria-label="移动">移动+3</button>
      <button class="dev-btn" @click="devAdd50" aria-label="批量添加">+50对象</button>
      <span class="dev-fps" aria-label="FPS">{{ devFps }} FPS</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, onUnmounted, watch, toRefs, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { exclusiveConfirm } from '@/utils/exclusiveConfirm'
import {
  Trash2,
  Check,
  Copy,
  Clipboard,
  ClipboardPaste,
  Grid2x2,
  Pencil,
  Lock,
  Unlock,
  Group,
  Ungroup,
} from 'lucide-vue-next'
import * as THREE from 'three'
import { useSceneStore, useSelectionStore } from '../stores/scene'
import { startViewLiveData, stopViewLiveData } from '../connect-data/viewLiveData'
import type { EditorSnapshot } from '../types/scene'
import { useModelLibraryStore } from '../stores/modelLibrary'
import { SceneManager } from '../three/SceneManager'
import {
  initObjectManager,
  syncObjects,
  makeSceneObject,
  fullRebuild,
  getThreeObject,
  setHighlight,
  setOnImportedModelLoaded,
  getInfoCardDisplayConfig,
  setInfoCardDisplayConfig,
  refreshInfoCards,
  revealTempInfoCard,
} from '../three/ObjectManager'
import { SelectHandler, type SelectionBoxBounds } from '../three/SelectHandler'
import { DragHandler, getClickWorldPosition } from '../three/DragHandler'
import { DragGhostPreview } from '../three/DragGhostPreview'
import { PipeSnapHelper } from '../three/PipeSnapHelper'
import { SnapHelper } from '../three/SnapHelper'
import { HistoryManager } from '../three/HistoryManager'
import { ScaleHandles } from '../three/ScaleHandles'
import { ConnectionManager } from '../three/ConnectionManager'
import BubbleMenu from './BubbleMenu.vue'
import Minimap from './Minimap.vue'
import ObjectListPanel from './ObjectListPanel.vue'
import PhysicsNavPanel from './PhysicsNavPanel.vue'
import PropertiesPanel from './PropertiesPanel.vue'
import PhysicsDetailPanel from './PhysicsDetailPanel.vue'
import { storeToRefs } from 'pinia'
import ImportModelDialog from './ImportModelDialog.vue'
import ArrayDuplicateDialog from './ArrayDuplicateDialog.vue'
import { getNonOverlappingPosition } from '../utils/spawnPosition'
import { pickSurfaceYFromPointer } from '../utils/surfaceSnap'
import type { RealtimeConnectionState } from '../types/realtime'
import type { CameraViewPreset } from '../types/scene'
import { cloneSceneObjectAt } from '../utils/cloneSceneObject'
import type { ArrayGridAxis } from '../utils/arrayDuplicate'
import { createReferencePlanAndFloorSlabs } from './scene-canvas/referencePlanAndFloorSlabs'
import { createMeasureTool } from './scene-canvas/measureTool'
import { createRealtimePipeline } from './scene-canvas/realtimePipeline'
import { createRotateTool } from './scene-canvas/rotateTool'
import { createSceneCanvasActions } from './scene-canvas/sceneCanvasActions'
import { createKeyboardShortcuts } from './scene-canvas/keyboardShortcuts'
import { applySceneEnvironmentConfig, applySceneCameraConfig } from './scene-canvas/applySceneEnvironment'
import { createPathTool } from './scene-canvas/pathTool'
import { createLayoutStrokeTool } from './scene-canvas/layoutStrokeTool'
import { createPolylineObjectHandles } from './scene-canvas/polylineObjectHandles'
import { createPipeConnectTool } from './scene-canvas/pipeConnectTool'
import { buildPipeTopology, toPipeObjects } from '../utils/pipeTopology'
import { collectLinkedPipePolylineUpdates } from '../utils/linkedPipePolyline'
import { registry } from '../components-lib/registry'
import { uniformScalePatchIfNeeded } from '../utils/foodWaterUniform'
import { groupObjectIds, ungroupObjectIds } from '@/utils/batchObjectOps'
import DashboardOverlay from '@/dashboard/DashboardOverlay.vue'
import HtmlOverlayHost from '@/components/HtmlOverlayHost.vue'
import type { HtmlOverlayConfig } from '@/types/scene'
import type { DashboardRuntimeSlice } from '@/dashboard/dashboardRuntimeTypes'
import WidgetInspector from '@/dashboard/editor/WidgetInspector.vue'
import { MIN_FRAC, MIN_FRAC_FREE } from '@/dashboard/editor/useDashboardGrid'
import ComposeWidgetList from '@/dashboard/editor/ComposeWidgetList.vue'
import { previewDashboardSliceFromLiveData } from '@/dashboard/previewSliceFromLiveData'
import { mergeSyntheticIntoSlice } from '@/dashboard/editor/mergeSyntheticDashboardSlice'
import { emptyDashboardSlice } from '@/dashboard/dashboardRuntimeTypes'
import type { DashboardConfig, WidgetDef } from '@/types/dashboardConfig'
import { defaultDashboardConfig } from '@/types/dashboardConfig'

const props = defineProps<{
  pureSceneMode?: boolean
  /** 编辑器 HTML 大屏覆盖层（场景层内容，渲染在 .scene-viewport 内；由结构性分层保证不盖系统 UI，见 z-layers.css） */
  htmlOverlay?: HtmlOverlayConfig | null
  htmlOverlaySlice?: DashboardRuntimeSlice | null
}>()
const { pureSceneMode } = toRefs(props)
const htmlOverlayProp = computed(() => props.htmlOverlay ?? null)
const htmlOverlaySliceProp = computed(() => props.htmlOverlaySlice ?? null)
const readonlyMode = computed(() => !!pureSceneMode.value)

const emit = defineEmits<{
  (e: 'space-key'): void
  (e: 'save-request'): void
  (e: 'realistic-mode-change', on: boolean): void
  (e: 'open-connect-data'): void
  (e: 'open-project-connect'): void
  (e: 'html-overlay-close'): void
}>()

const isDev = import.meta.env.DEV
const devFps = ref(0)

/** 场景层视口（3D 画布 mount 目标；绝对定位铺满画布，contain 锁死场景内容） */
const containerRef = ref<HTMLElement | null>(null)
/** 画布根（拖拽命中区用根矩形，不随 viewport 变） */
const rootRef = ref<HTMLElement | null>(null)
/** 场景浮层容器（临时信息卡 Teleport 目标；viewport 的兄弟、系统层之前） */
const viewportOverlayLayerRef = ref<HTMLElement | null>(null)
const minimapRef = ref<InstanceType<typeof Minimap> | null>(null)

const sceneStore = useSceneStore()
const selectionStore = useSelectionStore()
const modelLibraryStore = useModelLibraryStore()
const {
  dashboardComposeMode,
  composeRightPanelTab,
  rightPanelCollapsed,
  composeSelectedWidgetId,
  composeGridSize,
  composeSnapToGrid,
  editorPreviewMergedLiveData,
  simulationMonitorMode,
} = storeToRefs(sceneStore)
const composePositionClampMinFrac = computed(() =>
  composeSnapToGrid.value ? MIN_FRAC : MIN_FRAC_FREE,
)
/**
 * 罗盘（方位指示器）属于「场景设置」：编辑态显示用于指示方位；预览态（进入预览/新标签页对照）隐藏。
 * readonlyMode = 进入预览（pureSceneMode）；simulationMonitorMode = 新标签页对照页（走 store 标志，不传 pureSceneMode）。
 */
const showCompass = computed(
  () =>
    sceneStore.activeScene()?.scene?.showCompass !== false &&
    !readonlyMode.value &&
    !simulationMonitorMode.value,
)
const showMinimap = computed(() => sceneStore.activeScene()?.scene?.showMinimap !== false)
const { selectedId, selectedIds, bubblePos, currentFloorId, focusRequestId, draggingComponentType, measureToolActive, exclusiveEditFloorId, pathToolActive, editorTool, selectedPathId } =
  storeToRefs(selectionStore)

const showSceneRightPanels = computed(
  () => !readonlyMode.value && !simulationMonitorMode.value
)
const nonComposeRightPanelTab = computed<'scene' | 'physics'>(() =>
  composeRightPanelTab.value === 'physics' ? 'physics' : 'scene'
)

/**
 * 信息卡全关时的临时卡：
 * - 无大屏：CSS2D（revealTempInfoCard）
 * - 有大屏：HtmlOverlayHost Vue 浮层（labelRenderer z=2 在 ho-root z=40 之下，CSS2D 不可靠）
 */
function maybeRevealTempInfoCard(objectId: string | null) {
  const overlayOn = !!htmlOverlayProp.value && !dashboardComposeMode.value
  if (overlayOn && !getInfoCardDisplayConfig().globalShow) {
    setInfoCardDisplayConfig({ forceShowObjectId: null })
    refreshInfoCards(sceneStore.activeObjects())
    return
  }
  revealTempInfoCard(objectId)
}

watch([selectedId, bubblePos, focusRequestId], () => {
  const id = selectedId.value
  if (id) sceneStore.clearPhysicsEditSelection()
  maybeRevealTempInfoCard(id)
})

function onObjectListToggleHidden(id: string) {
  const obj = sceneStore.activeObjects().find((item) => item.id === id)
  if (!obj) return
  histManager.push(sceneStore.getEditorSnapshot())
  sceneStore.updateObject(id, { hidden: !obj.hidden })
}

const activeSceneData = computed(() => sceneStore.activeScene())

const dashboardConfigForCompose = computed((): DashboardConfig => {
  const sc = sceneStore.activeScene()
  const d = sc?.scene?.dashboardConfig
  if (d && d.version === 1) return d
  return defaultDashboardConfig()
})

const previewDashboardSlice = computed(() => {
  const sd = activeSceneData.value
  if (!sd?.objects?.length) return emptyDashboardSlice()
  const base = previewDashboardSliceFromLiveData(editorPreviewMergedLiveData.value, sd.objects)
  // 与组态态一致：绑定尚未对上 liveData 时填占位，避免观测/演示里整屏「— / 未知」
  return mergeSyntheticIntoSlice(base, dashboardConfigForCompose.value.widgets)
})

const showPreviewDashboardOverlay = computed(
  () =>
    !dashboardComposeMode.value &&
    !!activeSceneData.value &&
    dashboardConfigForCompose.value.widgets.length > 0 &&
    (simulationMonitorMode.value || !!pureSceneMode.value)
)

const selectedComposeWidget = computed(() => {
  const id = composeSelectedWidgetId.value
  if (!id) return null
  return dashboardConfigForCompose.value.widgets.find((w) => w.id === id) ?? null
})

function onPreviewDashboardFocus(objectId: string) {
  selectionStore.requestFocus(objectId)
  // 预览/编辑都给一个屏幕锚点，便于宿主临时信息卡定位
  const g = getThreeObject(objectId)
  const smNow = sm
  if (g && smNow) {
    const box = new THREE.Box3().setFromObject(g)
    const c = box.getCenter(new THREE.Vector3())
    c.project(smNow.camera)
    const rect = smNow.renderer.domElement.getBoundingClientRect()
    selectionStore.setBubblePos({
      x: rect.left + ((c.x + 1) / 2) * rect.width,
      y: rect.top + ((-c.y + 1) / 2) * rect.height,
    })
  }
  maybeRevealTempInfoCard(objectId)
}

function patchComposeDashboard(patch: Partial<DashboardConfig>) {
  histManager.push(sceneStore.getEditorSnapshot())
  const base = dashboardConfigForCompose.value
  sceneStore.updateSceneConfig({ dashboardConfig: { ...base, ...patch, version: 1 } })
}

function onComposeWidgetUpdate(patch: Partial<WidgetDef>) {
  const id = composeSelectedWidgetId.value
  if (!id) return
  const ws = dashboardConfigForCompose.value.widgets.map((w) =>
    w.id === id ? { ...w, ...patch } : w
  )
  patchComposeDashboard({ widgets: ws })
}

function onComposeWidgetDelete() {
  const id = composeSelectedWidgetId.value
  if (!id) return
  patchComposeDashboard({
    widgets: dashboardConfigForCompose.value.widgets.filter((w) => w.id !== id),
  })
  sceneStore.setComposeSelectedWidgetId(null)
}

function onComposeWidgetReorder(direction: 'front' | 'back') {
  const id = composeSelectedWidgetId.value
  if (!id) return
  const ws = [...dashboardConfigForCompose.value.widgets]
  const idx = ws.findIndex((w) => w.id === id)
  if (idx < 0) return
  const [item] = ws.splice(idx, 1)
  if (direction === 'front') ws.push(item)
  else ws.unshift(item)
  patchComposeDashboard({ widgets: ws })
}

function onComposeWidgetListSelect(id: string) {
  sceneStore.setComposeSelectedWidgetId(id || null)
  sceneStore.setComposeRightPanelTab('widget')
}

// 暴露给父组件
let sm: SceneManager
let selectHandler: SelectHandler
let dragHandler: DragHandler
let scaleHandles: ScaleHandles
let connectionManager: ConnectionManager

function isSceneObjectLocked(id: string): boolean {
  return !!sceneStore.activeObjects().find((o) => o.id === id)?.locked
}

/** 单选且未锁定时附着缩放控制柄 */
function attachScaleHandlesIfUnlocked(id: string) {
  if (transformMode.value === 'rotate') return
  if (isSceneObjectLocked(id)) {
    scaleHandles?.detach()
    return
  }
  scaleHandles?.attach(id)
}
let pipeSnapHelper: PipeSnapHelper
let snapHelper: SnapHelper
const histManager = new HistoryManager()

const floorEditBlocked = computed(
  () => !!exclusiveEditFloorId.value && currentFloorId.value !== exclusiveEditFloorId.value
)
const interactionReadonly = computed(
  () => readonlyMode.value || floorEditBlocked.value || simulationMonitorMode.value
)
const isMeasureModeActive = computed(() => !!selectionStore.measureToolActive)

function hasBlockingDraftInteraction() {
  return pathHasDraft.value || layoutHasDraft.value || pipeConnectHasDraft.value
}

/**
 * 是否拦截「点选」。
 * 预览态（pureSceneMode/presentation）仍要能点设备看临时信息卡；
 * 仅楼层锁定、仿真监测、测量/笔画草稿中禁止点选。
 */
function shouldBlockObjectSelection() {
  if (isMeasureModeActive.value || hasBlockingDraftInteraction()) return true
  if (floorEditBlocked.value || simulationMonitorMode.value) return true
  return false
}

function shouldBlockObjectDrag() {
  return interactionReadonly.value || isMeasureModeActive.value
}

/** 预览态点选：只更新选中/高亮/临时信息卡，不做连线、多选编辑、缩放柄等 */
function handlePreviewPick(id: string | null, screenPos: { x: number; y: number } | null) {
  if (id && screenPos) {
    if (selectedId.value && selectedId.value !== id) setHighlight(selectedId.value, false)
    selectionStore.select(id, screenPos)
    setHighlight(id, true)
    selectHandler?.setCurrentId(id)
    maybeRevealTempInfoCard(id)
    return
  }
  if (selectedId.value) setHighlight(selectedId.value, false)
  selectionStore.deselect()
  selectHandler?.setCurrentId(null)
  maybeRevealTempInfoCard(null)
}

function routePathSelectionClick(e: MouseEvent) {
  if (interactionReadonly.value || pathToolActive.value) return
  if (handlePolylineHandleClick(e)) {
    e.stopPropagation()
    e.preventDefault()
    selectionStore.setMeasureToolActive(false)
    return
  }
  if (handlePathClick(e)) {
    e.stopPropagation()
    e.preventDefault()
    selectionStore.setMeasureToolActive(false)
    selectedIds.value.forEach(id => setHighlight(id, false))
    selectionStore.deselect()
  }
}

function routePathPointerDown(e: PointerEvent) {
  if (interactionReadonly.value) return
  if (selectionStore.editorTool === 'pipeConnect') return
  if (startPolylineDrag(e)) {
    e.stopPropagation()
    e.preventDefault()
    return
  }
  if (pathToolActive.value) return
  if (startPathDrag(e)) {
    e.stopPropagation()
    e.preventDefault()
  }
}

/** 与 store 选中同步 3D 高亮（含从右侧对象列表、Ctrl+A 等不经画布点击的选中） */
let lastSyncedHighlightIds: string[] = []
watch(
  () => ({ sid: selectedId.value, sids: [...selectedIds.value] }),
  () => {
    // 楼层锁定 / 仿真监测：不跟选中刷高亮；预览态仍要刷高亮（便于看清点了哪台）
    if (floorEditBlocked.value || simulationMonitorMode.value) return
    const ids =
      selectedIds.value.length > 1
        ? [...selectedIds.value]
        : selectedId.value
          ? [selectedId.value]
          : []
    const nextSet = new Set(ids)
    for (const id of lastSyncedHighlightIds) {
      if (!nextSet.has(id)) setHighlight(id, false)
    }
    for (const id of ids) {
      if (!lastSyncedHighlightIds.includes(id)) setHighlight(id, true)
    }
    lastSyncedHighlightIds = [...ids]
    selectHandler?.setCurrentId(ids[0] ?? null)
    // 预览/只读：不高亮后挂缩放柄
    if (readonlyMode.value || interactionReadonly.value) {
      scaleHandles?.detach()
      return
    }
    if (transformMode.value === 'rotate') {
      scaleHandles?.detach()
    } else if (ids.length === 1) {
      attachScaleHandlesIfUnlocked(ids[0])
    } else {
      scaleHandles?.detach()
    }
  },
  { deep: true, flush: 'sync' }
)

const refPlanFloors = createReferencePlanAndFloorSlabs({
  getSm: () => sm,
  sceneStore,
  readonlyMode,
  currentFloorId,
  exclusiveEditFloorId,
})
const {
  disposeReferencePlanMesh,
  syncReferencePlanMesh,
  disposeFloorSlabs,
  syncFloorSlabs,
  syncExclusiveFloorVisibility,
} = refPlanFloors

const measureToolApi = createMeasureTool({
  getSm: () => sm,
  sceneStore,
  selectionStore,
  interactionReadonly,
})
const {
  measureHasDraft,
  onMeasureCanvasClick,
  onMeasureCanvasMove,
  teardownMeasureToolVisuals,
  clearMeasureDraft,
  hasMeasureDraftPoint,
  initMeasureRoot,
  detachMeasureRootFromScene,
} = measureToolApi

const pathToolApi = createPathTool({
  getSm: () => sm,
  sceneStore,
  selectionStore,
  histManager,
  interactionReadonly,
  currentFloorId,
})

const {
  syncPathsVisuals,
  appendDraftWaypointByClick,
  finishDraftPath,
  clearDraftPath,
  onPathDraftPointerMove,
  handlePathClick,
  deleteSelectedWaypoint,
  startDrag: startPathDrag,
  drag: dragPath,
  stopDrag: stopPathDrag,
  disposePathTool,
  hasDraft: pathHasDraft,
} = pathToolApi

const layoutStrokeApi = createLayoutStrokeTool({
  getSm: () => sm,
  sceneStore,
  selectionStore,
  histManager,
  interactionReadonly,
  currentFloorId,
  getEditorTool: () => selectionStore.editorTool,
  getRoadStrokeColor: () => selectionStore.roadStrokeColor,
  getRoadStrokeShowCenterLine: () => selectionStore.roadStrokeShowCenterLine,
  getRoadStrokeHasCurb: () => selectionStore.roadStrokeHasCurb,
  getRoadStrokeCurbColor: () => selectionStore.roadStrokeCurbColor,
  getPipePresetId: () => selectionStore.pipeStrokePresetId,
  getWallStrokeThickness: () => selectionStore.wallStrokeThickness,
  getWallStrokeHeight: () => selectionStore.wallStrokeHeight,
})

const {
  appendWaypoint: appendLayoutWaypoint,
  onDraftPointerMove: onLayoutDraftPointerMove,
  finishStroke: finishLayoutStroke,
  clearDraft: clearLayoutDraft,
  syncDraftVisuals: syncLayoutDraftVisuals,
  disposeLayoutStrokeTool,
  hasDraft: layoutHasDraft,
} = layoutStrokeApi

const pipeConnectApi = createPipeConnectTool({
  getSm: () => sm,
  sceneStore,
  selectionStore,
  histManager,
  interactionReadonly,
  currentFloorId,
  getEditorTool: () => selectionStore.editorTool,
  getPipePresetId: () => selectionStore.pipeStrokePresetId,
})

const {
  onPointerMove: onPipeConnectPointerMove,
  onCanvasClick: onPipeConnectCanvasClick,
  clearDraft: clearPipeConnectDraft,
  syncVisuals: syncPipeConnectVisuals,
  dispose: disposePipeConnectTool,
  hasDraft: pipeConnectHasDraft,
} = pipeConnectApi

const polylineHandlesApi = createPolylineObjectHandles({
  getSm: () => sm,
  sceneStore,
  selectionStore,
  histManager,
  interactionReadonly,
  currentFloorId,
  selectedId,
})

const {
  handlePolylineHandleClick,
  startPolylineDrag,
  dragPolyline,
  stopPolylineDrag,
  deleteSelectedPolylineWaypoint,
  syncPolylineHandles,
  disposePolylineHandles,
} = polylineHandlesApi

let measureClickListener: ((e: MouseEvent) => void) | null = null
let measureMoveListener: ((e: MouseEvent) => void) | null = null
let pathClickListener: ((e: MouseEvent) => void) | null = null
let layoutClickListener: ((e: MouseEvent) => void) | null = null
let pathDblClickListener: ((e: MouseEvent) => void) | null = null
let layoutDblClickListener: ((e: MouseEvent) => void) | null = null
let pipeConnectClickListener: ((e: MouseEvent) => void) | null = null
let pathSelectClickListener: ((e: MouseEvent) => void) | null = null
let pathPointerDownListener: ((e: PointerEvent) => void) | null = null
let pathPointerMoveListener: ((e: PointerEvent) => void) | null = null
let pathPointerUpListener: (() => void) | null = null

let historyUiCallback: (() => void) | undefined
function notifyHistoryUi() {
  historyUiCallback?.()
}
const _histPush = histManager.push.bind(histManager)
histManager.push = (snap: EditorSnapshot) => {
  _histPush(snap)
  notifyHistoryUi()
}

let autoSaveTimer: ReturnType<typeof setInterval>
const runtimeConnectionState = ref<RealtimeConnectionState>('idle')
const debugRealtimeEnabled = ref(false)
const runtimeBadgeVisible = ref(true)
const runtimeDataSource = ref<'mock' | 'live' | 'static'>('mock')
const runtimeIsStale = ref(false)
const lastSnapshotAt = ref<number | null>(null)
const runtimeConnectionLabel = computed(() => {
  if (runtimeConnectionState.value !== 'connected') return runtimeConnectionState.value
  if (runtimeDataSource.value === 'mock') return 'connected(mock)'
  if (runtimeDataSource.value === 'live') return 'connected(live)'
  return 'connected(static)'
})
const lastSnapshotAtText = computed(() => {
  if (!lastSnapshotAt.value) return ''
  const d = new Date(lastSnapshotAt.value)
  return `最后更新 ${d.toLocaleTimeString()}`
})

const {
  stopRealtimePipeline,
  setupStaticPipeline,
  setupPreviewPipeline,
} = createRealtimePipeline({
  getSm: () => sm,
  getConnectionManager: () => connectionManager,
  sceneStore,
  getCurrentFloorId: () => currentFloorId.value,
  runtimeConnectionState,
  debugRealtimeEnabled,
  runtimeBadgeVisible,
  runtimeDataSource,
  runtimeIsStale,
  lastSnapshotAt,
})

/**
 * 轴 A · 数据源唯一派发点：static / mock / live 三分支，跟随 store.getDataSourceMode() 这一真值。
 * 展示形态（组态/预览/观测）不改数据源；组态只是「展示形态」，始终用 mock 预览管线设计控件。
 * live 由 viewLiveData 轮询驱动；startViewLiveData / stopViewLiveData 只在这里统一调用（不再由任何 UI 直接调）。
 */
function applyDataSourceMode() {
  if (dashboardComposeMode.value) {
    stopViewLiveData()
    setupPreviewPipeline()
    return
  }
  const mode = sceneStore.getDataSourceMode()
  if (mode === 'live') {
    stopRealtimePipeline()
    const result = startViewLiveData('[data-source-mode]')
    if (!result.ok) {
      // live 不可用（未登录/无绑定设备）：交给 store 降级到 mock，本函数随后由 watch 再次进入 mock 分支
      sceneStore.setDataSourceMode('mock')
      return
    }
    return
  }
  if (mode === 'static') {
    stopViewLiveData()
    setupStaticPipeline()
    return
  }
  // mock
  stopViewLiveData()
  setupPreviewPipeline()
}

watch(
  () => sceneStore.getDataSourceMode(),
  () => applyDataSourceMode(),
)

// 右键菜单状态（objectId 为空时表示点在空白处）
const contextMenu = ref({ visible: false, x: 0, y: 0, objectId: '' })
const contextMenuRef = ref<HTMLElement | null>(null)

const CONTEXT_MENU_VIEWPORT_PAD = 8

function clampContextMenuToViewport() {
  const el = contextMenuRef.value
  if (!el || !contextMenu.value.visible) return
  const rect = el.getBoundingClientRect()
  const pad = CONTEXT_MENU_VIEWPORT_PAD
  const vw = document.documentElement.clientWidth
  const vh = document.documentElement.clientHeight
  let x = contextMenu.value.x
  let y = contextMenu.value.y
  if (x + rect.width > vw - pad) x = Math.max(pad, vw - pad - rect.width)
  if (y + rect.height > vh - pad) y = Math.max(pad, vh - pad - rect.height)
  if (x < pad) x = pad
  if (y < pad) y = pad
  if (x !== contextMenu.value.x || y !== contextMenu.value.y) {
    contextMenu.value = { ...contextMenu.value, x, y }
  }
}
const contextMenuTargetLocked = computed(() => {
  const id = contextMenu.value.objectId
  if (!id) return false
  return isSceneObjectLocked(id)
})
const contextMenuTargetHasGroup = computed(() => {
  const id = contextMenu.value.objectId
  if (!id) return false
  return !!sceneStore.activeObjects().find((o) => o.id === id)?.groupId
})
const contextMenuCanGroup = computed(() => selectedIds.value.length >= 2)
const realisticMode = ref(false)
const realisticBgUrl = ref('')
const bgOpacity = ref(0.8)
const fovValue = ref(60)
const realisticFileInputRef = ref<HTMLInputElement | null>(null)
let realisticPrevGridVisible: boolean | null = null
const canPaste = computed(() => clipboard.value.length > 0)
// 连线状态
const connectingFromId = ref<string | null>(null)
/** 当前在编辑属性的连线 id */
const selectedConnectionId = ref<string | null>(null)
const connectionBubblePos = ref<{ x: number; y: number } | null>(null)
const connectionLabelDraft = ref('')

watch(selectedConnectionId, (id) => {
  if (!id) {
    connectionLabelDraft.value = ''
    return
  }
  const c = sceneStore.activeConnections().find((x) => x.id === id)
  connectionLabelDraft.value = c?.label ?? ''
})

const activeConnectionColor = computed(() => {
  const id = selectedConnectionId.value
  if (!id) return '#4488cc'
  const c = sceneStore.activeConnections().find((x) => x.id === id)
  return c?.color ?? '#4488cc'
})
// 模型导入弹窗
const showImportDialog = ref(false)
const importInitialFiles = ref<File[]>([])
const arrayDialogVisible = ref(false)
// 模型文件拖入高亮
const isModelDragOver = ref(false)

// 内部剪贴板，存储已复制的对象快照（深拷贝，不持久化）
const clipboard = ref<ReturnType<typeof makeSceneObject>[]>([])

// 画布内最后鼠标位置，用于 Ctrl+V 粘贴到鼠标位置
const lastMouseInCanvas = ref<{ clientX: number; clientY: number } | null>(null)
// 框选矩形（橡皮筋）
const selectionBox = ref<SelectionBoxBounds | null>(null)

// 精确输入状态
const preciseInputRef = ref<HTMLInputElement | null>(null)
const preciseInput = ref({
  visible: false,
  x: 0,
  y: 0,
  label: '宽度',
  value: 10,
  paramKey: 'width'
})
const compassRotationDeg = ref(0)
const cameraForward = new THREE.Vector3()
const dragGhostPreview = new DragGhostPreview()
let cameraChangeListener: (() => void) | null = null
const vehicleProgressById = new Map<string, number>()
const pathCurveCache = new Map<string, { length: number; curve?: THREE.CatmullRomCurve3; points?: THREE.Vector3[]; segLens?: number[] }>()
let applyingPipeTopology = false
let applyingLinkedPipeRefresh = false

function refreshPipeTopology() {
  if (applyingPipeTopology) return
  const raw = sceneStore.activeObjects().filter(o => o.type === 'pipe_polyline')
  if (!raw.length) return
  const pipes = toPipeObjects(raw.map((o) => {
    const pos = o.position ?? [0, 0, 0]
    const wps = Array.isArray(o.params?.waypoints) ? o.params.waypoints : []
    const worldWaypoints = wps.map((p: any) => ({
      x: Number(p?.x ?? 0) + pos[0],
      y: Number(p?.y ?? 0) + pos[1],
      z: Number(p?.z ?? 0) + pos[2],
    }))
    return { id: o.id, params: { ...(o.params ?? {}), waypoints: worldWaypoints } }
  }))
  const topo = buildPipeTopology(pipes)
  const updates: Array<{ id: string; params: Record<string, any> }> = []
  for (const p of pipes) {
    const fittings = topo.fittingsByPipe.get(p.id) ?? []
    const nodes = topo.nodeByPipe.get(p.id) ?? []
    const src = raw.find(o => o.id === p.id)
    if (!src) continue
    const prevFittings = JSON.stringify(src.params?.autoFittings ?? [])
    const nextFittings = JSON.stringify(fittings)
    const prevNodes = JSON.stringify(src.params?.topologyNodes ?? [])
    const nextNodes = JSON.stringify(nodes)
    if (prevFittings !== nextFittings || prevNodes !== nextNodes) {
      const anchor = src.position ?? [0, 0, 0]
      const localFittings = fittings.map(f => ({
        ...f,
        x: f.x - anchor[0],
        y: f.y - anchor[1],
        z: f.z - anchor[2],
      }))
      const localNodes = nodes.map(n => ({
        x: n.x - anchor[0],
        y: n.y - anchor[1],
        z: n.z - anchor[2],
      }))
      updates.push({
        id: p.id,
        params: {
          ...src.params,
          autoFittings: localFittings,
          topologyNodes: localNodes,
        },
      })
    }
  }
  if (!updates.length) return
  applyingPipeTopology = true
  try {
    updates.forEach(u => sceneStore.updateObject(u.id, { params: u.params }))
  } finally {
    applyingPipeTopology = false
  }
}

watch(
  () => sceneStore.activeObjects()
    .filter(o => o.type === 'pipe_polyline')
    .map((o) => {
      const p = o.position ?? [0, 0, 0]
      const wps = Array.isArray(o.params?.waypoints) ? o.params.waypoints : []
      const dia = Number(o.params?.diameter ?? 0)
      const closed = (o.params?.closed === true) ? 1 : 0
      // 仅监听“驱动拓扑计算”的原始输入，排除 autoFittings/topologyNodes，避免 watch 自触发。
      return `${o.id}|${p[0]},${p[1]},${p[2]}|${dia}|${closed}|${JSON.stringify(wps)}`
    })
    .join('||'),
  () => {
    refreshPipeTopology()
  },
  { flush: 'post' }
)

function refreshLinkedPipePolylines() {
  if (applyingLinkedPipeRefresh) return
  const objs = sceneStore.activeObjects()
  const gridSize = sceneStore.activeScene()?.scene?.gridSize ?? 1
  const updates = collectLinkedPipePolylineUpdates(objs, gridSize)
  if (!updates.length) return
  applyingLinkedPipeRefresh = true
  try {
    for (const u of updates) {
      sceneStore.updateObject(u.id, { position: u.position, params: u.params })
    }
  } finally {
    applyingLinkedPipeRefresh = false
  }
}

watch(
  () => {
    const objs = sceneStore.activeObjects()
    const linkedIds = new Set<string>()
    for (const o of objs) {
      if (o.type !== 'pipe_polyline') continue
      const ep = (o.params as any)?.endpoints
      if (ep?.start?.objectId) linkedIds.add(ep.start.objectId)
      if (ep?.end?.objectId) linkedIds.add(ep.end.objectId)
    }
    if (linkedIds.size === 0) return ''
    return objs
      .filter((o) => linkedIds.has(o.id))
      .map((o) => {
        const p = o.position ?? [0, 0, 0]
        return [
          o.id,
          p[0], p[1], p[2],
          o.rotationY ?? 0,
          o.rotationX ?? 0,
          o.rotationZ ?? 0,
          JSON.stringify(o.params ?? {}),
        ].join('|')
      })
      .sort()
      .join('||')
  },
  () => {
    refreshLinkedPipePolylines()
  },
  { flush: 'post' },
)

const sceneCanvasActions = createSceneCanvasActions({
  getSm: () => sm,
  sceneStore,
  selectionStore,
  modelLibraryStore,
  histManager,
  interactionReadonly,
  currentFloorId,
  selectedId,
  selectedIds,
  draggingComponentType,
  contextMenu,
  connectingFromId,
  showImportDialog,
  importInitialFiles,
  arrayDialogVisible,
  isModelDragOver,
  clipboard,
  lastMouseInCanvas,
  dragGhostPreview,
  onMarkDirty: () => minimapRef.value?.markDirty(),
  doDelete,
  doDuplicate,
  deselect,
})

const {
  transformMode,
  rotateDragging,
  rotateDisplayDeg,
  rotateState,
  onRotateMouseDown,
  onRotateMouseMove,
  onRotateMouseUp,
  stopRotateInteraction,
} = createRotateTool({
  getSm: () => sm,
  sceneStore,
  selectedIds,
  onBeforeRotate: () => histManager.push(sceneStore.getEditorSnapshot()),
})

function clearConnectionSelection() {
  selectedConnectionId.value = null
  connectionBubblePos.value = null
}

async function deleteSelectedPathWithConfirm() {
  const pid = selectedPathId.value
  if (!pid) return
  const n = sceneStore.activeObjects().filter(o => o.pathId === pid).length
  const msg = n > 0
    ? `有 ${n} 辆车将失去路径分配，确定删除该路径？`
    : '确定删除该路径？'
  const ok = await exclusiveConfirm(msg, '删除确认')
  if (!ok) return
  histManager.push(sceneStore.getEditorSnapshot())
  sceneStore.removePath(pid)
  selectionStore.selectPath(null)
  pathCurveCache.delete(pid)
  syncPathsVisuals()
}

const { onKeyDown, onGlobalKey } = createKeyboardShortcuts({
  getSm: () => sm,
  sceneStore,
  selectionStore,
  histManager,
  interactionReadonly,
  currentFloorId,
  selectedId,
  selectedIds,
  clipboard,
  lastMouseInCanvas,
  transformMode,
  connectingFromId,
  selectedConnectionId,
  measureToolActive,
  hasMeasureDraftPoint,
  clearMeasureDraft,
  stopRotateInteraction,
  cancelConnecting: () => connectionManager?.cancelConnecting(),
  clearConnectionSelection,
  notifyHistoryUi,
  deselect,
  doDelete,
  doDuplicate,
  emitSpaceKey: () => emit('space-key'),
  requestSave: () => emit('save-request'),
  finishPathDraft: finishDraftPath,
  hasPathDraft: () => pathHasDraft.value,
  clearPathDraft: clearDraftPath,
  pathToolActive,
  hasLayoutDraft: () => layoutHasDraft.value,
  clearLayoutDraft,
  finishLayoutDraft: finishLayoutStroke,
  hasPipeConnectDraft: () => pipeConnectHasDraft.value,
  clearPipeConnectDraft,
  deleteSelectedPathWaypoint: () => deleteSelectedWaypoint(),
  deleteSelectedPolylineWaypoint: () => deleteSelectedPolylineWaypoint(),
  selectedPathId,
  deleteSelectedPath: deleteSelectedPathWithConfirm,
})

function pickObjectIdAtScreen(clientX: number, clientY: number): string | null {
  if (!sm) return null
  const canvas = sm.renderer.domElement
  const rect = canvas.getBoundingClientRect()
  const ndc = new THREE.Vector2(
    ((clientX - rect.left) / rect.width) * 2 - 1,
    -((clientY - rect.top) / rect.height) * 2 + 1
  )
  const raycaster = new THREE.Raycaster()
  raycaster.setFromCamera(ndc, sm.camera)
  raycaster.layers.set(0)
  const intersects = raycaster.intersectObjects(sm.scene.children, true)
  for (const hit of intersects) {
    if (hit.object.userData.interactable === false) continue
    if (hit.object.parent?.name === '__ground__' || hit.object.name === '__ground__') continue
    const oid = hit.object.userData.objectId as string | undefined
    if (oid) return oid
  }
  return null
}

function onCanvasContextMenu(e: MouseEvent) {
  if (interactionReadonly.value) return
  e.preventDefault()
  if (!sm) return
  const connId = connectionManager?.pickConnectionAt(e)
  if (connId) {
    histManager.push(sceneStore.getEditorSnapshot())
    sceneStore.removeConnection(connId)
    if (selectedConnectionId.value === connId) clearConnectionSelection()
    return
  }
  if (connectingFromId.value) {
    connectionManager?.cancelConnecting()
    connectingFromId.value = null
  }
  const oid = pickObjectIdAtScreen(e.clientX, e.clientY)
  if (oid) {
    const preserveMulti =
      selectedIds.value.length > 1 && selectedIds.value.includes(oid)
    if (preserveMulti) {
      selectionStore.setBubblePos({ x: e.clientX, y: e.clientY })
      selectHandler?.setCurrentId(oid)
      if (transformMode.value !== 'rotate') scaleHandles?.detach()
    } else {
      if (selectedId.value && selectedId.value !== oid) setHighlight(selectedId.value, false)
      selectionStore.select(oid, { x: e.clientX, y: e.clientY })
      setHighlight(oid, true)
      selectHandler?.setCurrentId(oid)
      if (transformMode.value !== 'rotate') attachScaleHandlesIfUnlocked(oid)
    }
  }
  clearConnectionSelection()
  contextMenu.value = { visible: true, x: e.clientX, y: e.clientY, objectId: oid ?? '' }
  void nextTick(() => clampContextMenuToViewport())
}

function onConnectionColorInput(e: Event) {
  const id = selectedConnectionId.value
  if (!id) return
  const v = (e.target as HTMLInputElement).value
  histManager.push(sceneStore.getEditorSnapshot())
  sceneStore.updateConnection(id, { color: v })
}

function commitConnectionLabel() {
  const id = selectedConnectionId.value
  if (!id) return
  histManager.push(sceneStore.getEditorSnapshot())
  sceneStore.updateConnection(id, { label: connectionLabelDraft.value })
}

function deleteSelectedConnection() {
  const id = selectedConnectionId.value
  if (!id) return
  histManager.push(sceneStore.getEditorSnapshot())
  sceneStore.removeConnection(id)
  clearConnectionSelection()
}

function updateCompassRotation() {
  if (!sm) return
  sm.camera.getWorldDirection(cameraForward)
  const yawRad = Math.atan2(cameraForward.x, cameraForward.z)
  compassRotationDeg.value = -THREE.MathUtils.radToDeg(yawRad)
}

function drawImageCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  width: number,
  height: number
) {
  const targetRatio = width / height
  const sourceRatio = img.width / img.height
  if (sourceRatio > targetRatio) {
    const drawW = img.height * targetRatio
    const sx = (img.width - drawW) / 2
    ctx.drawImage(img, sx, 0, drawW, img.height, 0, 0, width, height)
    return
  }
  const drawH = img.width / targetRatio
  const sy = (img.height - drawH) / 2
  ctx.drawImage(img, 0, sy, img.width, drawH, 0, 0, width, height)
}

function syncRealisticVisualState() {
  if (!sm) return
  const ground = sm.scene.getObjectByName('__ground__') as THREE.Mesh | null
  const groundMaterial = ground?.material as THREE.MeshStandardMaterial | undefined
  if (realisticMode.value) {
    sm.scene.background = null
    if (groundMaterial) {
      groundMaterial.transparent = true
      groundMaterial.opacity = 0
      groundMaterial.needsUpdate = true
    }
    if (realisticPrevGridVisible == null) {
      realisticPrevGridVisible = sm.getGridVisible()
    }
    sm.setGridVisible(false)
  } else {
    const bg = sceneStore.activeScene()?.scene?.background ?? '#d0e8f0'
    sm.setSolidBackground(bg)
    if (groundMaterial) {
      groundMaterial.opacity = 1
      groundMaterial.transparent = false
      groundMaterial.needsUpdate = true
    }
    if (realisticPrevGridVisible != null) {
      sm.setGridVisible(realisticPrevGridVisible)
      realisticPrevGridVisible = null
    }
  }
  sm.markDirty()
}

function revokeRealisticBgUrl() {
  if (!realisticBgUrl.value) return
  URL.revokeObjectURL(realisticBgUrl.value)
  realisticBgUrl.value = ''
}

function enterRealisticMode() {
  realisticMode.value = true
  if (sm) fovValue.value = Math.round(sm.camera.fov)
  syncRealisticVisualState()
  emit('realistic-mode-change', true)
}

function exitRealisticMode() {
  revokeRealisticBgUrl()
  realisticMode.value = false
  syncRealisticVisualState()
  emit('realistic-mode-change', false)
}

function toggleRealisticMode() {
  if (realisticMode.value) {
    exitRealisticMode()
    return
  }
  enterRealisticMode()
}

function openRealisticUpload() {
  if (!realisticMode.value) enterRealisticMode()
  realisticFileInputRef.value?.click()
}

function onBgOpacityInput(raw: string) {
  const v = Number(raw)
  if (Number.isNaN(v)) return
  bgOpacity.value = Math.max(0, Math.min(1, v))
}

function onFovInput(raw: string) {
  const v = Number(raw)
  if (Number.isNaN(v)) return
  fovValue.value = Math.max(30, Math.min(90, Math.round(v)))
}

function onRealisticFileChange(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  if (!file.type.startsWith('image/')) {
    ElMessage.warning('仅支持上传图片文件。')
    input.value = ''
    return
  }
  revokeRealisticBgUrl()
  realisticBgUrl.value = URL.createObjectURL(file)
  if (!realisticMode.value) enterRealisticMode()
  input.value = ''
}

async function exportComposite() {
  if (!sm) return
  try {
    if (!realisticBgUrl.value) {
      ElMessage.warning('请先上传实景照片，再导出合成图。')
      return
    }

    const rendererCanvas = sm.renderer.domElement
    const width = rendererCanvas.width
    const height = rendererCanvas.height
    const exportCanvas = document.createElement('canvas')
    exportCanvas.width = width
    exportCanvas.height = height
    const ctx = exportCanvas.getContext('2d')
    if (!ctx) return

    // 1) 先画实景背景（始终以上传图为准，避免 DOM 层级/重排导致漏画）
    const img = new Image()
    img.src = realisticBgUrl.value
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('背景图片加载失败'))
    })
    ctx.globalAlpha = bgOpacity.value
    drawImageCover(ctx, img, width, height)
    ctx.globalAlpha = 1

    // 2) 再画 Three 结果（导出时临时隐藏地板/网格，确保“模型叠加”）
    const ground = sm.scene.getObjectByName('__ground__') as THREE.Mesh | null
    const gridWasVisible = sm.getGridVisible()
    const groundWasVisible = ground?.visible ?? true
    if (ground) ground.visible = false
    sm.setGridVisible(false)
    sm.renderer.render(sm.scene, sm.camera)
    ctx.drawImage(rendererCanvas, 0, 0, width, height)
    if (ground) ground.visible = groundWasVisible
    sm.setGridVisible(gridWasVisible)
    sm.markDirty()

    exportCanvas.toBlob((blob) => {
      if (!blob) {
        ElMessage.error('导出失败，请稍后重试。')
        return
      }
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `factory-scene-composite-${Date.now()}.png`
      a.click()
      URL.revokeObjectURL(url)
    }, 'image/png')
  } catch (e) {
    console.error('导出合成图失败:', e)
    ElMessage.error('导出合成图失败，请确认已上传实景照片后重试。')
  }
}

function getVehicleSpeed(obj: any) {
  const speedCandidates = [obj?.params?.speed, obj?.params?.moveSpeed, obj?.params?.velocity]
  const speed = speedCandidates.find((v) => typeof v === 'number')
  return typeof speed === 'number' && Number.isFinite(speed) && speed > 0 ? speed : 2
}

function getPathRuntimeData(path: any) {
  const cached = pathCurveCache.get(path.id)
  const points = path.waypoints.map((p: any) => new THREE.Vector3(p.x, p.y, p.z))
  const cacheKey = JSON.stringify(path.waypoints) + `|${path.closed}|${path.smooth}`
  if (cached && (cached as any).key === cacheKey) return cached

  if (path.smooth) {
    const curve = new THREE.CatmullRomCurve3(points, path.closed, 'catmullrom', 0.1)
    const next = { length: curve.getLength(), curve, key: cacheKey } as any
    pathCurveCache.set(path.id, next)
    return next
  }
  const segLens: number[] = []
  let total = 0
  for (let i = 0; i < points.length - 1; i++) {
    const l = points[i]!.distanceTo(points[i + 1]!)
    segLens.push(l)
    total += l
  }
  if (path.closed && points.length > 1) {
    const closingLen = points[points.length - 1]!.distanceTo(points[0]!)
    segLens.push(closingLen)
    total += closingLen
  }
  const next = { length: total, points, segLens, key: cacheKey } as any
  pathCurveCache.set(path.id, next)
  return next
}

function samplePathAt(path: any, distance: number) {
  const runtime = getPathRuntimeData(path)
  const len = Math.max(runtime.length, 0.0001)
  const s = Math.max(0, Math.min(len, distance))
  if (path.smooth && runtime.curve) {
    const u = s / len
    return {
      pos: runtime.curve.getPointAt(u),
      tangent: runtime.curve.getTangentAt(u),
      length: len,
    }
  }
  const points = runtime.points as THREE.Vector3[]
  const segLens = runtime.segLens as number[]
  let acc = 0
  for (let i = 0; i < segLens.length; i++) {
    const l = segLens[i]!
    if (acc + l >= s || i === segLens.length - 1) {
      const t = l > 0 ? (s - acc) / l : 0
      const a = points[i]!
      const b = (path.closed && i === segLens.length - 1)
        ? points[0]!
        : points[i + 1]!
      return {
        pos: a.clone().lerp(b, t),
        tangent: b.clone().sub(a).normalize(),
        length: len,
      }
    }
    acc += l
  }
  return { pos: points[0]?.clone() ?? new THREE.Vector3(), tangent: new THREE.Vector3(1, 0, 0), length: len }
}

function updateVehiclePathFollow(_timeSec: number, deltaSec: number) {
  if (!sm || deltaSec <= 0) return
  const paths = sceneStore.activePaths()
  const pathMap = new Map(paths.map(p => [p.id, p]))
  let moved = false
  for (const obj of sceneStore.activeObjects()) {
    if (!obj.pathId) continue
    const path = pathMap.get(obj.pathId)
    if (!path || path.waypoints.length < 2) continue
    const data = getPathRuntimeData(path)
    const len = Math.max(data.length, 0.001)
    const speedLimit = path.speedLimit == null ? Number.POSITIVE_INFINITY : Math.max(0, path.speedLimit)
    const speed = Math.min(getVehicleSpeed(obj), speedLimit)
    const dir = obj.direction === 'backward' ? -1 : 1
    const base = vehicleProgressById.has(obj.id)
      ? vehicleProgressById.get(obj.id)!
      : Math.max(0, Math.min(1, obj.pathOffset ?? 0)) * len
    let next = base + speed * deltaSec * dir
    const loop = obj.loop === true
    if (loop) {
      next = ((next % len) + len) % len
    } else {
      next = Math.max(0, Math.min(len, next))
    }
    vehicleProgressById.set(obj.id, next)
    const sample = samplePathAt(path, next)
    const g = getThreeObject(obj.id)
    if (!g) continue
    g.position.set(sample.pos.x, sample.pos.y, sample.pos.z)
    g.userData.followPathDrive = true
    const yaw = Math.atan2(sample.tangent.z, sample.tangent.x)
    const targetYaw = -yaw + Math.PI / 2
    g.rotation.y = THREE.MathUtils.lerp(g.rotation.y, targetYaw, 0.25)
    moved = true
  }
  if (moved) {
    connectionManager?.syncConnections(sceneStore.activeConnections())
    sm.markDirty()
  }
}

onMounted(() => {
  if (!containerRef.value) return

  sm = new SceneManager()
  sm.mount(containerRef.value)
  fovValue.value = Math.round(sm.camera.fov)
  sm.addFrameCallback(updateVehiclePathFollow)
  updateCompassRotation()
  cameraChangeListener = () => updateCompassRotation()
  sm.controls?.addEventListener('change', cameraChangeListener)
  initObjectManager(sm)
  dragGhostPreview.setSceneManager(sm)

  // 坐标轴指示器：置于物体 3D 几何中心，长度按选中物体包围盒自动计算（厚实建筑也能看清）
  sm.setSelectionPositionGetter(() => {
    const ids = selectedIds.value.length > 1
      ? selectedIds.value
      : (selectedId.value ? [selectedId.value] : [])
    if (!ids.length) return null
    const box = new THREE.Box3()
    const center = new THREE.Vector3()
    const sizeVec = new THREE.Vector3()
    let cx = 0, cy = 0, cz = 0, n = 0
    let unionBox: THREE.Box3 | null = null
    for (const id of ids) {
      const g = getThreeObject(id)
      if (g) {
        box.setFromObject(g)
        box.getCenter(center)
        cx += center.x
        cy += center.y
        cz += center.z
        n++
        if (!unionBox) unionBox = box.clone()
        else unionBox.union(box)
      }
    }
    if (!n || !unionBox) return null
    unionBox.getSize(sizeVec)
    const maxDim = Math.max(sizeVec.x, sizeVec.y, sizeVec.z)
    const axisLen = Math.max(2, Math.min(35, maxDim * 0.45))
    return { x: cx / n, y: cy / n, z: cz / n, size: axisLen, maxDim }
  })

  // 将 sceneStore 挂到 window 供 ScaleHandles 访问（非响应式桥接）
  ;(window as any).__sceneStore = sceneStore

  // 初始同步
  syncObjects(sceneStore.activeObjects(), currentFloorId.value)
  syncReferencePlanMesh()
  syncFloorSlabs()
  syncExclusiveFloorVisibility()
  syncCameraFromActiveScene()
  applyDataSourceMode()

  // SelectHandler：点击选中对象；Alt+左键拖拽（空白处）框选多选；Alt+点击空白处在不重叠位置复制当前选中建筑
  selectHandler = new SelectHandler(
    sm,
    (id, screenPos, isShift, event) => {
    // 预览态：允许点选查看临时信息卡，禁止后续编辑分支
    if (readonlyMode.value) {
      handlePreviewPick(id, screenPos)
      return
    }
    if (interactionReadonly.value) return
    if (id && screenPos) {
      clearConnectionSelection()
      if (connectingFromId.value) {
        const fromObj = sceneStore.activeObjects().find(o => o.id === connectingFromId.value)
        const toObj = sceneStore.activeObjects().find(o => o.id === id)
        const f1 = fromObj?.floorId ?? currentFloorId.value
        const f2 = toObj?.floorId ?? currentFloorId.value
        if (fromObj && toObj && f1 !== f2) {
          connectionManager?.cancelConnecting()
          connectingFromId.value = null
          return
        }
        const conn = connectionManager?.finishConnecting(id)
        if (conn) {
          histManager.push(sceneStore.getEditorSnapshot())
          sceneStore.addConnection(conn)
        }
        connectingFromId.value = null
        return
      }
      if (isShift && selectionStore.selectedPathId && id) {
        histManager.push(sceneStore.getEditorSnapshot())
        sceneStore.updateObject(id, { pathId: selectionStore.selectedPathId })
        pathCurveCache.delete(selectionStore.selectedPathId)
        return
      }
      if (isShift && selectedId.value) {
        selectionStore.multiSelect(id, screenPos)
      } else {
        if (selectedId.value && selectedId.value !== id) setHighlight(selectedId.value, false)
        const groupId = sceneStore.activeObjects().find(o => o.id === id)?.groupId
        if (groupId) {
          const memberIds = sceneStore.activeObjects().filter(o => o.groupId === groupId).map(o => o.id)
          if (memberIds.length > 1) {
            selectionStore.select(memberIds[0], screenPos)
            memberIds.slice(1).forEach(mid => selectionStore.multiSelect(mid, screenPos))
          } else {
            selectionStore.select(id, screenPos)
          }
        } else {
          selectionStore.select(id, screenPos)
        }
        // 全关信息卡时：点选立刻弹该台（无大屏走 CSS2D；有大屏走 HtmlOverlayHost）
        maybeRevealTempInfoCard(id)
        // 附加缩放控制柄
        if (transformMode.value !== 'rotate') attachScaleHandlesIfUnlocked(id)
      }
    } else {
      if (connectingFromId.value) {
        connectionManager?.cancelConnecting()
        connectingFromId.value = null
        return
      }
      // Alt+点击空白：在点击处生成不重叠的副本
      if (event?.altKey && selectedId.value && sm) {
        const source = sceneStore.activeObjects().find(o => o.id === selectedId.value)
        if (source) {
          const canvas = sm.renderer.domElement
          const floor = sceneStore.activeFloors().find(f => f.id === currentFloorId.value)
          const yElevation = floor?.elevation ?? 0
          const clickPos = getClickWorldPosition(event, canvas, sm.camera, { groundY: yElevation })
          if (clickPos) {
            const existing = sceneStore.activeObjects()
            const position = getNonOverlappingPosition(
              clickPos.x, clickPos.z, source.type, source.params ?? {},
              existing, yElevation
            )
            if (position) {
              histManager.push(sceneStore.getEditorSnapshot())
              const newObj = cloneSceneObjectAt(source, position)
              newObj.floorId = currentFloorId.value
              sceneStore.addObject(newObj)
              minimapRef.value?.markDirty()
            }
          }
        }
        return
      }
      deselect()
    }
  },
    {
      onSelectionBox: (b) => { selectionBox.value = b },
      onBoxSelect: (ids, centerScreen) => {
        if (readonlyMode.value) {
          handlePreviewPick(ids[0] ?? null, centerScreen)
          return
        }
        clearConnectionSelection()
        if (!ids.length) {
          deselect()
          return
        }
        selectedIds.value.forEach(id => setHighlight(id, false))
        ids.forEach(id => setHighlight(id, true))
        selectionStore.select(ids[0], centerScreen)
        ids.slice(1).forEach(id => selectionStore.multiSelect(id, centerScreen))
        if (transformMode.value !== 'rotate') attachScaleHandlesIfUnlocked(ids[0])
      },
      shouldBlockSelection: shouldBlockObjectSelection,
      onConnectionClick: (connId, e) => {
        if (interactionReadonly.value) return
        if (selectedId.value) setHighlight(selectedId.value, false)
        scaleHandles?.detach()
        selectionStore.deselect()
        selectHandler?.setCurrentId(null)
        selectedConnectionId.value = connId
        connectionBubblePos.value = { x: e.clientX, y: e.clientY }
      },
      shouldDeferConnectionPick: () => !!connectingFromId.value,
    }
  )

  pipeSnapHelper = new PipeSnapHelper(sm)
  snapHelper = new SnapHelper()

  // DragHandler：对象在场景内拖动（需要阻止控制柄区域触发）；Shift+多选时拖动可整体移动
  dragHandler = new DragHandler(
    sm,
    (id, pos) => {
      pipeSnapHelper.clear()
      histManager.push(sceneStore.getEditorSnapshot())
      const o = sceneStore.activeObjects().find(x => x.id === id)
      const g = getThreeObject(id)
      if (g) g.userData.followPathDrive = false
      vehicleProgressById.delete(id)
      const patch: { position: [number, number, number]; pathId?: null } = { position: pos }
      if (o?.pathId) patch.pathId = null
      sceneStore.updateObject(id, patch)
      minimapRef.value?.markDirty()
    },
    () => (e: PointerEvent) => {
      if (transformMode.value === 'rotate') return true
      const checker = scaleHandles?.isHandleAt.bind(scaleHandles)
      return checker ? checker(e) : false
    },
    {
      onDragMove: (id, x, z) => {
        pipeSnapHelper.updateIndicators(x, z, sceneStore.activeObjects(), id)
        // 拖拽过程中对象先移动 Three.js 节点，需实时重算连线锚点
        connectionManager?.syncConnections(sceneStore.activeConnections())
      },
      getSnapPosition: (x, z, excludeId) => {
        const pipeSnap = pipeSnapHelper.snapPosition(x, z, sceneStore.activeObjects(), excludeId)
        if (pipeSnap) return pipeSnap
        return snapHelper.snapPosition(x, z, sceneStore.activeObjects(), excludeId ?? undefined)
      },
      getSurfaceSnapY: (e, excludeIds) => {
        if (!sm) return 0
        const floor = sceneStore.activeFloors().find(f => f.id === currentFloorId.value)
        const floorY = floor?.elevation ?? 0
        return pickSurfaceYFromPointer(
          e,
          sm.renderer.domElement,
          sm.camera,
          sm.scene,
          sceneStore.activeObjects(),
          floorY,
          { excludeIds, getThreeObject },
        )
      },
      getMultiDragContext: (e: MouseEvent, dragObjectId: string) => {
        const objs = sceneStore.activeObjects()
        // 成组后：拖任意组内对象即整组一起移动（无需 Shift）
        const obj = objs.find(o => o.id === dragObjectId)
        if (obj?.groupId) {
          const group = sceneStore.activeGroups().find(g => g.id === obj.groupId)
          if (group && group.memberIds.length > 1) {
            return {
              ids: group.memberIds,
              getPosition: (id: string) => objs.find(o => o.id === id)?.position
            }
          }
        }
        // 未成组的多选：需按住 Shift 才一起拖动
        if (!e.shiftKey) return null
        const ids = selectedIds.value
        if (ids.length <= 1 || !ids.includes(dragObjectId)) return null
        return {
          ids,
          getPosition: (id: string) => objs.find(o => o.id === id)?.position
        }
      },
      onMultiDragEnd: (updates) => {
        pipeSnapHelper.clear()
        histManager.push(sceneStore.getEditorSnapshot())
        updates.forEach(({ id, position }) => {
          const g = getThreeObject(id)
          if (g) g.userData.followPathDrive = false
          vehicleProgressById.delete(id)
          const o = sceneStore.activeObjects().find(x => x.id === id)
          const patch: { position: [number, number, number]; pathId?: null } = { position }
          if (o?.pathId) patch.pathId = null
          sceneStore.updateObject(id, patch)
        })
        minimapRef.value?.markDirty()
      },
      shouldBlockDrag: shouldBlockObjectDrag,
      isObjectLocked: (id: string) => isSceneObjectLocked(id),
    }
  )

  // ScaleHandles：缩放控制柄
  scaleHandles = new ScaleHandles(
    sm,
    () => { histManager.push(sceneStore.getEditorSnapshot()) },
    (id, updates) => {
      sceneStore.updateObjectParams(id, updates)
      const obj = sceneStore.activeObjects().find(o => o.id === id)
      // 导入模型的 userScale 不需要 fullRebuild（syncObjects 已处理 scale 同步）
      if (obj && obj.type !== 'imported_model' && obj.type !== 'dxf_reference') {
        fullRebuild(obj)
      }
      minimapRef.value?.markDirty()
    },
    () => { /* resize end */ }
  )
  scaleHandles.onPreciseInput = (_ht, paramKey, val, label, cx, cy) => {
    preciseInput.value = { visible: true, x: cx, y: cy, label, value: val, paramKey }
    nextTick(() => preciseInputRef.value?.focus())
  }

  setOnImportedModelLoaded((objectId) => {
    if (interactionReadonly.value) return
    if (selectedId.value === objectId || selectedIds.value.includes(objectId)) {
      scaleHandles?.refreshPosition()
    }
  })

  // ConnectionManager：设备连线
  connectionManager = new ConnectionManager(sm)

  // 同步连线
  connectionManager.syncConnections(sceneStore.activeConnections())
  syncPathsVisuals()

  initMeasureRoot()

  watch(
    () => selectionStore.measureToolActive,
    (on) => {
      if (!sm) return
      const canvas = sm.renderer.domElement
      if (measureClickListener) {
        canvas.removeEventListener('click', measureClickListener, true)
        measureClickListener = null
      }
      if (measureMoveListener) {
        canvas.removeEventListener('mousemove', measureMoveListener)
        measureMoveListener = null
      }
      if (on && !interactionReadonly.value) {
        deselect()
        measureClickListener = onMeasureCanvasClick
        measureMoveListener = onMeasureCanvasMove
        canvas.addEventListener('click', measureClickListener, true)
        canvas.addEventListener('mousemove', measureMoveListener)
      } else {
        teardownMeasureToolVisuals()
      }
    }
  )

  pathSelectClickListener = routePathSelectionClick
  sm.renderer.domElement.addEventListener('click', pathSelectClickListener, true)

  function detachPathLayoutListeners() {
    if (!sm) return
    const canvas = sm.renderer.domElement
    if (pathClickListener) {
      canvas.removeEventListener('click', pathClickListener, true)
      pathClickListener = null
    }
    if (layoutClickListener) {
      canvas.removeEventListener('click', layoutClickListener, true)
      layoutClickListener = null
    }
    if (pathDblClickListener) {
      canvas.removeEventListener('dblclick', pathDblClickListener, true)
      pathDblClickListener = null
    }
    if (layoutDblClickListener) {
      canvas.removeEventListener('dblclick', layoutDblClickListener, true)
      layoutDblClickListener = null
    }
    if (pipeConnectClickListener) {
      canvas.removeEventListener('click', pipeConnectClickListener, true)
      pipeConnectClickListener = null
    }
  }

  watch(
    () => selectionStore.editorTool,
    (tool) => {
      if (!sm) return
      const canvas = sm.renderer.domElement
      detachPathLayoutListeners()
      clearDraftPath()
      clearLayoutDraft()
      clearPipeConnectDraft()
      if (interactionReadonly.value) {
        syncPathsVisuals()
        syncLayoutDraftVisuals()
        syncPipeConnectVisuals()
        return
      }
      if (tool === 'path') {
        selectionStore.setMeasureToolActive(false)
        deselect()
        pathClickListener = (e: MouseEvent) => {
          if (appendDraftWaypointByClick(e)) {
            e.stopPropagation()
            e.preventDefault()
          }
        }
        pathDblClickListener = (e: MouseEvent) => {
          finishDraftPath()
          e.stopPropagation()
          e.preventDefault()
        }
        canvas.addEventListener('click', pathClickListener, true)
        canvas.addEventListener('dblclick', pathDblClickListener, true)
      } else if (tool === 'roadStroke' || tool === 'wallStroke' || tool === 'riverStroke' || tool === 'pipeStroke') {
        selectionStore.setMeasureToolActive(false)
        layoutClickListener = (e: MouseEvent) => {
          if (!layoutHasDraft.value) {
            const hitId = pickObjectIdAtScreen(e.clientX, e.clientY)
            if (hitId) return
          }
          if (appendLayoutWaypoint(e)) {
            e.stopPropagation()
            e.preventDefault()
          }
        }
        layoutDblClickListener = (e: MouseEvent) => {
          finishLayoutStroke()
          e.stopPropagation()
          e.preventDefault()
        }
        canvas.addEventListener('click', layoutClickListener, true)
        canvas.addEventListener('dblclick', layoutDblClickListener, true)
      } else if (tool === 'pipeConnect') {
        selectionStore.setMeasureToolActive(false)
        pipeConnectClickListener = (e: MouseEvent) => {
          if (onPipeConnectCanvasClick(e)) {
            e.stopPropagation()
            e.preventDefault()
          }
        }
        canvas.addEventListener('click', pipeConnectClickListener, true)
      }
      syncPathsVisuals()
      syncLayoutDraftVisuals()
      syncPipeConnectVisuals()
    }
  )

  watch(readonlyMode, (ro) => {
    if (ro) {
      selectionStore.setMeasureToolActive(false)
      selectionStore.setEditorTool('none')
      selectionStore.deselect()
      scaleHandles?.detach()
    }
  })

  // canvas 上的 mousemove：更新控制柄鼠标样式、记录最后位置供 Ctrl+V 粘贴
  sm.renderer.domElement.addEventListener('mousemove', (e) => {
    lastMouseInCanvas.value = { clientX: e.clientX, clientY: e.clientY }
    if (interactionReadonly.value) return
    if (selectionStore.editorTool === 'path') onPathDraftPointerMove(e)
    if (
      selectionStore.editorTool === 'roadStroke'
      || selectionStore.editorTool === 'wallStroke'
      || selectionStore.editorTool === 'riverStroke'
      || selectionStore.editorTool === 'pipeStroke'
    ) {
      onLayoutDraftPointerMove(e)
    }
    if (selectionStore.editorTool === 'pipeConnect') onPipeConnectPointerMove(e)
    if (transformMode.value === 'rotate') {
      sm.renderer.domElement.style.cursor = rotateState.active ? 'grabbing' : 'crosshair'
    } else {
      scaleHandles?.updateCursor(e)
    }
    // 连线预览
    if (connectingFromId.value) {
      connectionManager?.updatePreviewLine(connectingFromId.value, e.clientX, e.clientY)
    }
  })
  sm.renderer.domElement.addEventListener('mousedown', onRotateMouseDown)
  pathPointerDownListener = routePathPointerDown
  pathPointerMoveListener = (e: PointerEvent) => {
    dragPolyline(e)
    dragPath(e)
  }
  pathPointerUpListener = () => {
    stopPolylineDrag()
    stopPathDrag()
  }
  sm.renderer.domElement.addEventListener('pointerdown', pathPointerDownListener, true)
  window.addEventListener('pointermove', pathPointerMoveListener)
  window.addEventListener('pointerup', pathPointerUpListener)
  window.addEventListener('mousemove', onRotateMouseMove)
  window.addEventListener('mouseup', onRotateMouseUp)

  // 让 canvas 支持 HTML5 drop
  sm.renderer.domElement.addEventListener('dragover', (e) => {
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
  })
  // drop 事件由外层 Vue div 的 @drop.prevent="onDrop" 统一处理，此处不重复绑定

  // 键盘快捷键
  window.addEventListener('keydown', onKeyDown)

  // 自动保存 5分钟
  autoSaveTimer = setInterval(() => sceneStore.save(), 5 * 60 * 1000)

  // 关闭右键菜单
  window.addEventListener('click', closeContextMenu)
  window.addEventListener('keydown', onGlobalKey)
})

onUnmounted(() => {
  revokeRealisticBgUrl()
  emit('realistic-mode-change', false)
  dragGhostPreview.dispose()
  dragGhostPreview.setSceneManager(null)
  if (cameraChangeListener) {
    sm?.controls?.removeEventListener('change', cameraChangeListener)
    cameraChangeListener = null
  }
  sm?.unmount()
  sm?.removeFrameCallback(updateVehiclePathFollow)
  selectHandler?.dispose()
  dragHandler?.dispose()
  scaleHandles?.dispose()
  connectionManager?.dispose()
  pipeSnapHelper?.dispose()
  window.removeEventListener('keydown', onKeyDown)
  sm?.renderer?.domElement?.removeEventListener('mousedown', onRotateMouseDown)
  window.removeEventListener('mousemove', onRotateMouseMove)
  window.removeEventListener('mouseup', onRotateMouseUp)
  window.removeEventListener('click', closeContextMenu)
  window.removeEventListener('keydown', onGlobalKey)
  clearInterval(autoSaveTimer)
  stopRealtimePipeline()
  disposeReferencePlanMesh()
  disposeFloorSlabs()
  if (measureClickListener && sm?.renderer.domElement) {
    sm.renderer.domElement.removeEventListener('click', measureClickListener, true)
    measureClickListener = null
  }
  if (pathClickListener && sm?.renderer.domElement) {
    sm.renderer.domElement.removeEventListener('click', pathClickListener, true)
    pathClickListener = null
  }
  if (pathDblClickListener && sm?.renderer.domElement) {
    sm.renderer.domElement.removeEventListener('dblclick', pathDblClickListener, true)
    pathDblClickListener = null
  }
  if (layoutClickListener && sm?.renderer.domElement) {
    sm.renderer.domElement.removeEventListener('click', layoutClickListener, true)
    layoutClickListener = null
  }
  if (layoutDblClickListener && sm?.renderer.domElement) {
    sm.renderer.domElement.removeEventListener('dblclick', layoutDblClickListener, true)
    layoutDblClickListener = null
  }
  if (pipeConnectClickListener && sm?.renderer.domElement) {
    sm.renderer.domElement.removeEventListener('click', pipeConnectClickListener, true)
    pipeConnectClickListener = null
  }
  if (pathSelectClickListener && sm?.renderer.domElement) {
    sm.renderer.domElement.removeEventListener('click', pathSelectClickListener, true)
    pathSelectClickListener = null
  }
  if (pathPointerDownListener && sm?.renderer.domElement) {
    sm.renderer.domElement.removeEventListener('pointerdown', pathPointerDownListener, true)
    pathPointerDownListener = null
  }
  if (pathPointerMoveListener) {
    window.removeEventListener('pointermove', pathPointerMoveListener)
    pathPointerMoveListener = null
  }
  if (pathPointerUpListener) {
    window.removeEventListener('pointerup', pathPointerUpListener)
    pathPointerUpListener = null
  }
  teardownMeasureToolVisuals()
  detachMeasureRootFromScene()
  disposePathTool()
  disposeLayoutStrokeTool()
  disposePipeConnectTool()
  disposePolylineHandles()
  delete (window as any).__sceneStore
  setOnImportedModelLoaded(null)
})

// 监听 store 中 objects 变化 → 同步到 Three.js
watch(
  () => sceneStore.activeObjects(),
  (newObjs) => {
    if (!sm) return
    const idSet = new Set(newObjs.map(o => o.id))
    for (const [id] of vehicleProgressById) {
      if (!idSet.has(id)) vehicleProgressById.delete(id)
    }
    syncObjects(newObjs, currentFloorId.value)
    syncPolylineHandles()
    // 对象移动/缩放/重建后，连线需基于最新包围盒重算
    connectionManager?.syncConnections(sceneStore.activeConnections())
    syncExclusiveFloorVisibility()
    if (selectedId.value) {
      const obj3d = getThreeObject(selectedId.value)
      if (!obj3d) selectionStore.deselect()
      else scaleHandles?.refreshPosition()
    }
    minimapRef.value?.markDirty()
  },
  { deep: true }
)

watch(
  transformMode,
  (mode) => {
    if (!scaleHandles) return
    if (mode === 'rotate') {
      // 旋转模式与缩放控制柄互斥，避免拖拽时误触发尺寸变化
      scaleHandles.detach()
      return
    }
    if (interactionReadonly.value) {
      scaleHandles.detach()
      return
    }
    if (selectedIds.value.length === 1) attachScaleHandlesIfUnlocked(selectedIds.value[0])
    else scaleHandles.detach()
  },
  { flush: 'sync' }
)

watch(
  () => sceneStore.activeObjects().map((o) => `${o.id}:${o.locked ? 1 : 0}`).join('|'),
  () => {
    if (!scaleHandles) return
    if (transformMode.value === 'rotate' || interactionReadonly.value) {
      scaleHandles.detach()
      return
    }
    if (selectedIds.value.length === 1) attachScaleHandlesIfUnlocked(selectedIds.value[0])
    else scaleHandles.detach()
  },
  { flush: 'sync' }
)

watch(
  [
    () => sceneStore.activeScene()?.scene?.referencePlan,
    currentFloorId,
    () => sceneStore.activeTabId,
    () => sceneStore.activeFloors(),
  ],
  () => syncReferencePlanMesh(),
  { deep: true }
)

watch(
  [
    () => sceneStore.activeFloors(),
    currentFloorId,
    exclusiveEditFloorId,
    () => sceneStore.activeScene()?.scene?.ground?.size,
    readonlyMode,
  ],
  () => syncFloorSlabs(),
  { deep: true }
)

// 监听连线变化
watch(
  () => sceneStore.activeConnections(),
  (conns) => {
    connectionManager?.syncConnections(conns)
  },
  { deep: true }
)

watch(
  () => sceneStore.activePaths(),
  () => {
    pathCurveCache.clear()
    syncPathsVisuals()
  },
  { deep: true }
)

watch(
  () => selectionStore.selectedPathId,
  () => {
    syncPathsVisuals()
  }
)

function isPathVehicleType(t: string) {
  return t.includes('forklift') || t.includes('vehicle') || t.includes('agv')
}

let lastPathVehicleHighlightIds: string[] = []
watch(
  () => [selectionStore.selectedPathId, sceneStore.activeObjects()],
  () => {
    for (const id of lastPathVehicleHighlightIds) setHighlight(id, false)
    lastPathVehicleHighlightIds = []
    const pid = selectionStore.selectedPathId
    if (!pid) return
    for (const o of sceneStore.activeObjects()) {
      if (!isPathVehicleType(o.type)) continue
      if (o.pathId === pid) {
        setHighlight(o.id, true)
        lastPathVehicleHighlightIds.push(o.id)
      }
    }
  },
  { deep: true }
)

// 拖拽开始时立即在 Canvas 边缘显示 Ghost，鼠标一进入即可见（不等 dragover）
watch(draggingComponentType, (type) => {
  if (!sm) return
  if (type) {
    const canvas = sm.renderer.domElement
    const rect = canvas.getBoundingClientRect()
    // 取 Canvas 左侧偏内一点，作为 Ghost 待命位置（从左侧托盘拖入时一进来就可见）
    const clientX = rect.left + 40
    const clientY = rect.top + rect.height / 2
    const floor = sceneStore.activeFloors().find(f => f.id === currentFloorId.value)
    const yElevation = floor?.elevation ?? 0
    dragGhostPreview.showAtPosition(type, clientX, clientY, yElevation, sceneStore.activeObjects())
  } else {
    dragGhostPreview.dispose()
  }
})

// 监听楼层切换
watch(currentFloorId, (floorId) => {
  deselect()
  syncObjects(sceneStore.activeObjects(), floorId)
  syncFloorSlabs()
  syncExclusiveFloorVisibility()
  // 摄像机升降
  const floor = sceneStore.activeFloors().find(f => f.id === floorId)
  if (floor && sm) {
    const offset = floor.elevation * 0.3
    sm.camera.position.y += offset - (sm.camera.position.y - 60) * 0.1
    sm.controls.update()
  }
})

// 监听 activeTabId / 场景切换：同步视口相机与画布内容
watch(
  () => [sceneStore.activeTabId, sceneStore.activeScene()?.meta?.id] as const,
  () => {
    histManager.clear()
    deselect()
    if (selectHandler) selectHandler.setCurrentId(null)
    syncObjects(sceneStore.activeObjects(), currentFloorId.value)
    syncFloorSlabs()
    syncExclusiveFloorVisibility()
    syncReferencePlanMesh()
    connectionManager?.syncConnections(sceneStore.activeConnections())
    syncCameraFromActiveScene()
    applyDataSourceMode()
  }
)

// 新建工程：工程 id 变化时恢复默认 FOV / 退出实景模式
watch(
  () => sceneStore.projectMeta.id,
  () => {
    resetViewportSessionDefaults()
    syncCameraFromActiveScene()
  }
)

// mock 参数（角标/性能优先/刷新间隔）变化时重走 mock 管线；不含 dataSourceMode（已由上面的 watch 单独接管）
watch(
  () => {
    const rt = sceneStore.getRealtimeDebugConfig()
    return [rt.showBadge, rt.preferPerformance, rt.batchMs]
  },
  () => applyDataSourceMode(),
)

watch(simulationMonitorMode, (on) => {
  if (on) {
    selectionStore.deselect()
    selectionStore.setMeasureToolActive(false)
    selectionStore.setEditorTool('none')
  }
  applyDataSourceMode()
})

watch(dashboardComposeMode, () => {
  applyDataSourceMode()
})

watch(
  () => sceneStore.activeScene()?.dataIntegration,
  () => {
    applyDataSourceMode()
  },
  { deep: true }
)

watch(
  [exclusiveEditFloorId, () => sceneStore.activeFloors()],
  () => {
    if (exclusiveEditFloorId.value && currentFloorId.value !== exclusiveEditFloorId.value) {
      selectionStore.setFloor(exclusiveEditFloorId.value)
    }
    syncFloorSlabs()
    syncExclusiveFloorVisibility()
  },
  { deep: true }
)

// 监听场景设置变化
watch(
  () => sceneStore.activeScene()?.scene,
  (config) => {
    if (!sm || !config) return
    applySceneEnvironmentConfig(sm, config)
    syncRealisticVisualState()
  },
  { deep: true }
)

watch(
  () => fovValue.value,
  (nextFov) => {
    if (!sm) return
    sm.camera.fov = nextFov
    sm.camera.updateProjectionMatrix()
    sm.markDirty()
  }
)

function onDragOver(e: DragEvent) {
  sceneCanvasActions.onDragOver(e)
}

function onDragLeave(e: DragEvent) {
  sceneCanvasActions.onDragLeave(e, rootRef.value)
}

function onDrop(e: DragEvent) {
  sceneCanvasActions.onDrop(e)
}

function objectHasPath(oid: string) {
  return !!sceneStore.activeObjects().find(o => o.id === oid)?.pathId
}

function ctxAssignPathToObject() {
  const oid = contextMenu.value.objectId
  const pid = selectedPathId.value
  if (!oid || !pid) return
  histManager.push(sceneStore.getEditorSnapshot())
  sceneStore.updateObject(oid, { pathId: pid })
  pathCurveCache.delete(pid)
  closeContextMenu()
}

function ctxRemoveObjectPath() {
  const oid = contextMenu.value.objectId
  if (!oid) return
  const o = sceneStore.activeObjects().find(x => x.id === oid)
  const g = getThreeObject(oid)
  histManager.push(sceneStore.getEditorSnapshot())
  sceneStore.updateObject(oid, {
    pathId: null,
    position: g ? [g.position.x, g.position.y, g.position.z] : (o?.position ?? [0, 0, 0]),
  })
  if (g) g.userData.followPathDrive = false
  vehicleProgressById.delete(oid)
  closeContextMenu()
}

function ctxToggleObjectLock() {
  const oid = contextMenu.value.objectId
  if (!oid) return
  histManager.push(sceneStore.getEditorSnapshot())
  sceneStore.updateObject(oid, { locked: !contextMenuTargetLocked.value })
  if (selectedIds.value.length === 1 && selectedIds.value[0] === oid) {
    attachScaleHandlesIfUnlocked(oid)
  } else {
    scaleHandles?.detach()
  }
  closeContextMenu()
}

function ctxGroupFromMenu() {
  if (!contextMenuCanGroup.value) return
  histManager.push(sceneStore.getEditorSnapshot())
  groupObjectIds(sceneStore, selectedIds.value)
  closeContextMenu()
}

function ctxUngroupFromMenu() {
  const oid = contextMenu.value.objectId
  if (!oid || !contextMenuTargetHasGroup.value) return
  histManager.push(sceneStore.getEditorSnapshot())
  ungroupObjectIds(sceneStore, [oid])
  closeContextMenu()
}

function closeContextMenu() {
  sceneCanvasActions.closeContextMenu()
}

function startConnect() {
  sceneCanvasActions.startConnect(connectionManager)
}

function deleteSelected() {
  sceneCanvasActions.deleteSelected()
}

function ctxCopy() {
  sceneCanvasActions.ctxCopy()
}

function ctxPaste() {
  sceneCanvasActions.ctxPaste()
}

function ctxDuplicate() {
  sceneCanvasActions.ctxDuplicate()
}

function openArrayFromContext() {
  sceneCanvasActions.openArrayFromContext()
}

function ctxRename() {
  sceneCanvasActions.ctxRename()
}

function onArrayDuplicateConfirm(cfg: {
  rows: number
  cols: number
  rowSpacing: number
  colSpacing: number
  axis: ArrayGridAxis
}) {
  sceneCanvasActions.onArrayDuplicateConfirm(cfg)
}

function ctxSelectAll() {
  sceneCanvasActions.ctxSelectAll()
}

// 小地图导航
function onMinimapNavigate(x: number, z: number) {
  if (!sm) return
  const offset = sm.camera.position.y
  sm.controls.target.set(x, 0, z)
  sm.camera.position.set(x + offset * 0.5, offset, z - offset * 0.5)
  sm.controls.update()
}

// 对象列表面板 focus 请求：将相机移到指定对象中心，并设置 bubblePos 以显示气泡菜单（含隐藏对象）
watch(focusRequestId, (id) => {
  if (!id || !sm) return
  const obj = sceneStore.activeObjects().find(o => o.id === id)
  if (!obj) return
  const [ox = 0, oy = 0, oz = 0] = obj.position
  const camY = sm.camera.position.y
  sm.controls.target.set(ox, oy, oz)
  sm.camera.position.set(ox + camY * 0.3, camY, oz - camY * 0.3)
  sm.controls.update()

  // 计算对象在屏幕上的位置，使 BubbleMenu 可显示（隐藏对象无法点击，需从列表选中后点「显示」）
  const group = getThreeObject(id)
  const center = new THREE.Vector3(ox, oy, oz)
  if (group) {
    const box = new THREE.Box3().setFromObject(group)
    box.getCenter(center)
  }
  center.project(sm.camera)
  const canvas = sm.renderer.domElement
  const rect = canvas.getBoundingClientRect()
  const screenX = ((center.x + 1) / 2) * rect.width + rect.left
  const screenY = ((-center.y + 1) / 2) * rect.height + rect.top
  selectionStore.setBubblePos({ x: screenX, y: screenY })

  selectionStore.focusRequestId = null
})

function doDelete(id: string) {
  histManager.push(sceneStore.getEditorSnapshot())
  deselect()
  sceneStore.removeObject(id)
}

function doDuplicate(id: string) {
  const obj = sceneStore.activeObjects().find(o => o.id === id)
  if (!obj) return
  histManager.push(sceneStore.getEditorSnapshot())
  const offsetX = (obj.params?.width ?? obj.params?.size ?? 3) + 1
  const preferredX = obj.position[0] + offsetX
  const preferredZ = obj.position[2]
  const yElevation = obj.position[1]
  const existing = sceneStore.activeObjects()
  const position = getNonOverlappingPosition(preferredX, preferredZ, obj.type, obj.params ?? {}, existing, yElevation)
    ?? [preferredX, yElevation, preferredZ]
  const newObj = cloneSceneObjectAt(obj, position)
  sceneStore.addObject(newObj)
}

function deselect() {
  selectionStore.selectPath(null)
  clearConnectionSelection()
  if (selectedId.value) setHighlight(selectedId.value, false)
  scaleHandles?.detach()
  selectionStore.deselect()
  selectHandler?.setCurrentId(null)
  maybeRevealTempInfoCard(null)
}

// ── 开发模式测试辅助 ───────────────────────────────────────
function devSelectFirst() {
  const objs = sceneStore.activeObjects()
  if (!objs.length || !sm) return
  const obj = objs[0]
  const obj3d = getThreeObject(obj.id)
  if (!obj3d) return
  if (selectedId.value) setHighlight(selectedId.value, false)
  setHighlight(obj.id, true)
  selectHandler?.setCurrentId(obj.id)
  const box = new THREE.Box3().setFromObject(obj3d)
  const top = new THREE.Vector3((box.min.x + box.max.x) / 2, box.max.y, (box.min.z + box.max.z) / 2)
  top.project(sm.camera)
  const canvas = sm.renderer.domElement
  const rect = canvas.getBoundingClientRect()
  const x = ((top.x + 1) / 2) * rect.width + rect.left
  const y = ((-top.y + 1) / 2) * rect.height + rect.top
  selectionStore.select(obj.id, { x, y })
  if (transformMode.value !== 'rotate') attachScaleHandlesIfUnlocked(obj.id)
}

function devMoveSelected() {
  const id = selectedId.value
  if (!id) return
  const obj = sceneStore.activeObjects().find(o => o.id === id)
  if (!obj) return
  histManager.push(sceneStore.getEditorSnapshot())
  sceneStore.updateObject(id, { position: [obj.position[0] + 3, 0, obj.position[2]] })
}

const PERF_TYPES = [
  'building_factory', 'building_warehouse', 'building_office',
  'building_wall', 'building_gate', 'building_guardhouse',
  'infra_road', 'infra_greenery', 'infra_floor_zone',
  'equipment_conveyor_straight', 'equipment_shelf'
]

function devAdd50() {
  for (let i = 0; i < 50; i++) {
    const type = PERF_TYPES[i % PERF_TYPES.length]
    const x = Math.round((Math.random() - 0.5) * 100)
    const z = Math.round((Math.random() - 0.5) * 100)
    sceneStore.addObject(makeSceneObject(type, [x, 0, z]))
  }
}

// 精确输入确认
function confirmPreciseInput() {
  if (!selectedId.value || !preciseInput.value.value) return
  const val = Math.max(0.5, preciseInput.value.value)
  histManager.push(sceneStore.getEditorSnapshot())
  const obj = sceneStore.activeObjects().find(o => o.id === selectedId.value)
  const pk = preciseInput.value.paramKey
  let patch: Record<string, number> = { [pk]: val }
  if (obj) {
    const def = registry.get(obj.type)
    if (def) {
      const u = uniformScalePatchIfNeeded(def, obj.params, pk, val)
      if (u) patch = u
    }
  }
  sceneStore.updateObjectParams(selectedId.value, patch)
  if (obj) fullRebuild(obj)
  preciseInput.value.visible = false
}

// FPS 计数器
let _fpsFrames = 0
let _fpsLast = performance.now()
function _trackFps() {
  _fpsFrames++
  const now = performance.now()
  if (now - _fpsLast >= 1000) {
    devFps.value = _fpsFrames
    _fpsFrames = 0
    _fpsLast = now
  }
  requestAnimationFrame(_trackFps)
}
_trackFps()

function syncCameraFromActiveScene() {
  if (!sm) return
  const sceneConfig = sceneStore.activeScene()?.scene
  if (!sceneConfig?.camera) return
  applySceneCameraConfig(sm, sceneConfig)
  updateCompassRotation()
}

function resetViewportSessionDefaults() {
  if (!sm) return
  fovValue.value = 60
  sm.camera.fov = 60
  sm.camera.updateProjectionMatrix()
  if (realisticMode.value) exitRealisticMode()
}

function applyCameraPreset(kind: CameraViewPreset) {
  if (!sm) return
  const data = sceneStore.activeScene()
  if (kind === 'reset' && data?.scene?.camera) {
    const { position, target } = data.scene.camera
    sm.setViewFromConfig(position, target)
    return
  }
  if (kind === 'top') sm.setViewTop()
  else if (kind === 'isometric') sm.setViewIsometric()
}

function getCameraState(): { position: [number, number, number]; target: [number, number, number] } | null {
  if (!sm) return null
  const p = sm.camera.position
  const t = sm.controls.target
  return {
    position: [p.x, p.y, p.z],
    target: [t.x, t.y, t.z],
  }
}

function setCameraState(position: [number, number, number], target: [number, number, number]) {
  if (!sm) return
  sm.setViewFromConfig(position, target)
}

function setHistoryUiCallback(cb: (() => void) | undefined) {
  historyUiCallback = cb
}

// 暴露给父组件
defineExpose({
  getSceneManager: () => sm,
  getHistoryManager: () => histManager,
  doDelete,
  doDuplicate,
  onKeyDown,
  applyCameraPreset,
  getCameraState,
  setCameraState,
  setHistoryUiCallback,
  toggleRealisticMode,
  enterRealisticMode,
  exitRealisticMode,
  exportComposite,
})
</script>

<style scoped>
.data-binding-overlay {
  position: fixed;
  inset: 0;
  z-index: 4500;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}
.data-binding-modal {
  background: #1a2535;
  border: 1px solid #3a5070;
  border-radius: 10px;
  padding: 12px 14px;
  max-width: 440px;
  max-height: 90vh;
  overflow: auto;
}
.data-binding-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  color: #e0f0ff;
  font-weight: 600;
  font-size: 14px;
}
.data-binding-x {
  background: transparent;
  border: none;
  color: #9bb4d2;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.scene-canvas {
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  overflow: hidden;
  background: #d0e8f0;
  transition: box-shadow 0.2s;
}

/* ===== 场景层视口：唯一场景内容容器（3D / HTML 大屏 / 信息卡 / 罗盘）。
   contain: layout paint = 场景内容锁进本容器（fixed 后代也以其为包含块）+ 独立层叠上下文；
   z-index 保持 0（auto 亦可），后面的系统 UI 兄弟（200+）天然在其上。
   3D canvas 的 display/userSelect 由 SceneManager.mount 以 inline 样式单点声明，
   不再在此写 :deep(canvas)（会误命中 Minimap/TrendLine 等其他 canvas）。 ===== */
.scene-viewport {
  position: absolute;
  inset: 0;
  overflow: hidden;
  contain: layout paint;
  z-index: 0;
}

/* 场景浮层容器：临时信息卡等场景内浮层（viewport 的兄弟、系统层之前，z-index 兜底） */
.viewport-overlay-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: var(--z-scene-temp);
}

.realistic-bg-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  z-index: var(--z-scene-bg);
}

.realistic-hidden-input {
  display: none;
}

.scene-canvas.model-drag-over {
  box-shadow: inset 0 0 0 3px #3b82f6;
}

.simulation-viz-placeholder {
  position: absolute;
  left: 12px;
  bottom: 56px;
  z-index: var(--z-scene-hint);
  max-width: min(320px, 42vw);
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.88);
  border: 1px solid rgba(59, 130, 246, 0.45);
  color: #93c5fd;
  font-size: 11px;
  line-height: 1.45;
  pointer-events: none;
}

.right-panels {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 300px;
  min-width: 300px;
  max-width: 300px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  /* 外壳不接鼠标：列表收起 / 属性空闲时，露出的网格要点到画布，而不是拖选标题文字 */
  pointer-events: none;
  z-index: var(--z-sys-right-panels);
}

/* 收起/展开：往右滑出 + 淡出，而不是硬切换 display */
.right-panel-collapse-enter-active,
.right-panel-collapse-leave-active {
  transition: transform 0.18s ease, opacity 0.18s ease;
}
.right-panel-collapse-enter-from,
.right-panel-collapse-leave-to {
  transform: translateX(24px);
  opacity: 0;
}

/* 普通模式与组态模式共用：必须占满 .right-panels 高度，否则内层 flex + overflow 无法形成可滚动区域 */
.right-panels .compose-right-tabs {
  display: flex;
  flex-direction: column;
  flex: 1 1 0%;
  min-height: 0;
  min-width: 0;
  width: 100%;
  overflow: hidden;
  pointer-events: none;
}


.scene-right-stack {
  width: 300px;
  min-width: 300px;
  max-width: 300px;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.compose-right-tab-body,
.compose-tab-pane--scene,
.compose-tab-pane--physics,
.compose-tab-pane--widget,
.scene-right-stack,
.compose-scene-stack {
  pointer-events: none;
}

.compose-right-tab-body {
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.compose-tab-pane--scene {
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.compose-tab-pane--physics {
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.compose-tab-pane--physics > .scene-right-stack {
  flex: 1;
  min-height: 0;
  height: auto;
}

.compose-scene-stack {
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.compose-tab-pane--widget {
  flex: 1 1 0%;
  min-height: 0;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
  overflow-x: hidden;
}

.compose-widget-panel-inner {
  flex: 1 1 0%;
  min-height: 0;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  padding: 10px;
  overflow: hidden;
  /* 与 Tab 栏、属性面板同一底色，避免「灰条贴补丁」感 */
  background: rgba(15, 23, 42, 0.97);
  pointer-events: auto;
}

.compose-widget-panel-inner :deep(.inspector) {
  flex: 1 1 0%;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

/* 属性区与容器融为一块，仅内部卡片分层 */
.compose-widget-panel-inner :deep(.inspector.inspector--side) {
  background: transparent;
}

.compose-widget-panel-inner :deep(.in-footer) {
  background: transparent;
}

.realistic-overlay-panel {
  position: absolute;
  right: 314px;
  top: 10px;
  width: 256px;
  z-index: var(--z-sys-realistic-panel);
  pointer-events: auto;
  background: rgba(14, 24, 40, 0.92);
  border: 1px solid rgba(100, 160, 240, 0.3);
  border-radius: 10px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  backdrop-filter: blur(6px);
}

.realistic-title {
  font-size: 13px;
  color: #d8ecff;
  font-weight: 700;
}

.realistic-row {
  display: grid;
  grid-template-columns: 72px 1fr auto;
  gap: 8px;
  align-items: center;
  font-size: 11px;
  color: #a6bfd8;
}

.realistic-row input[type='range'] {
  width: 100%;
}

.realistic-btn {
  width: 100%;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #3a4a60;
  background: #243040;
  color: #c0d8f0;
  font-size: 12px;
  cursor: pointer;
}

.realistic-btn:hover {
  border-color: #4a9eff;
  background: #2a3b55;
}

.realistic-btn.primary {
  border-color: #2f6bc0;
  background: #1f4f8e;
}

.realistic-btn.danger {
  border-color: #7a3030;
  background: #3a1a1a;
  color: #ffb2b2;
}

.right-panels :deep(.obj-list-panel),
.right-panels :deep(.properties-panel),
.right-panels :deep(.physics-nav),
.right-panels :deep(.pdp) {
  pointer-events: auto;
}

.selection-box {
  position: fixed;
  border: 2px solid #4a9eff;
  background: rgba(74, 158, 255, 0.15);
  pointer-events: none;
  z-index: var(--z-sys-hint);
}

.model-drop-hint {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.12);
  color: #93c5fd;
  font-size: 18px;
  font-weight: 500;
  pointer-events: none;
  z-index: var(--z-sys-hint);
  letter-spacing: 0.02em;
}

.context-menu {
  position: fixed;
  z-index: var(--z-sys-menu);
  background: #1a2535;
  border: 1px solid #3a5070;
  border-radius: 6px;
  padding: 4px 0;
  min-width: 200px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
}

.ctx-item {
  padding: 7px 14px;
  font-size: 12px;
  color: #c0d8f0;
  cursor: pointer;
  transition: background 0.1s;
}

.ctx-item:hover { background: #243a55; }
.ctx-item.danger { color: #ff6677; }
.ctx-item.danger:hover { background: #3a1520; }
.ctx-item.disabled { color: #445566; pointer-events: none; }

.ctx-item-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.ctx-item-row-label {
  display: inline-flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.ctx-kbd {
  flex-shrink: 0;
  padding: 2px 6px;
  font-family: ui-monospace, 'Cascadia Mono', 'Segoe UI', monospace;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  color: rgba(160, 190, 220, 0.85);
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(100, 160, 240, 0.28);
  border-radius: 4px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.ctx-kbd-combo {
  font-size: 9px;
  letter-spacing: -0.02em;
  padding: 3px 5px;
}

.ctx-icon {
  vertical-align: -0.2em;
  margin-right: 6px;
  display: inline-block;
}

.ctx-separator {
  height: 1px;
  background: #2a3a50;
  margin: 2px 0;
}

.ctx-emoji {
  margin-right: 6px;
  font-size: 13px;
}

.connection-bubble {
  position: fixed;
  z-index: var(--z-sys-connection);
  transform: translate(-8px, -100%);
  margin-top: -8px;
  min-width: 200px;
  padding: 10px 12px;
  background: #1a2535;
  border: 1px solid #3a6090;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45);
}

.connection-bubble-title {
  font-size: 12px;
  font-weight: 600;
  color: #9ec8f0;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #2a4058;
}

.connection-bubble-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 11px;
  color: #a8c4e0;
  margin-bottom: 8px;
}

.connection-bubble-row input[type='color'] {
  width: 36px;
  height: 24px;
  padding: 0;
  border: 1px solid #3a5070;
  border-radius: 4px;
  cursor: pointer;
  background: transparent;
}

.connection-bubble-input {
  flex: 1;
  min-width: 0;
  padding: 4px 8px;
  font-size: 11px;
  color: #e0eef8;
  background: #0f1820;
  border: 1px solid #3a5070;
  border-radius: 4px;
}

.connection-bubble-del {
  width: 100%;
  margin-top: 4px;
  padding: 6px;
  font-size: 11px;
  color: #ff8899;
  background: #2a1520;
  border: 1px solid #553040;
  border-radius: 4px;
  cursor: pointer;
}

.connection-bubble-del:hover {
  background: #3a1a28;
}

.connect-hint {
  position: absolute;
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(20, 50, 90, 0.9);
  border: 1px solid #4a9eff;
  color: #88ccff;
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 12px;
  pointer-events: none;
  z-index: var(--z-sys-hint);
}

.runtime-connection {
  position: absolute;
  top: 0;
  left: 14px;
  z-index: var(--z-sys-badge);
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  border: 1px solid #3a5070;
  background: rgba(15, 25, 40, 0.86);
  color: #c0d8f0;
}

.floor-edit-lock-hint {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--z-sys-badge);
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid #f3b93f;
  background: rgba(46, 36, 12, 0.86);
  color: #ffd273;
  font-size: 11px;
  pointer-events: none;
}

/* 调试全屏预览时与顶边留出安全距，略上移避免视觉悬空过大 */
.runtime-connection.with-debug-banner {
  top: calc(10px + 11px + 12px + 2px);
}

.runtime-connection.state-connected {
  border-color: #16a34a;
  color: #86efac;
}

.runtime-connection.state-reconnecting,
.runtime-connection.state-connecting {
  border-color: #f59e0b;
  color: #fde68a;
}

.runtime-connection.state-offline,
.runtime-connection.state-error {
  border-color: #ef4444;
  color: #fecaca;
}

.rotate-hint {
  bottom: 86px;
  background: rgba(97, 52, 14, 0.9);
  border-color: #f59e0b;
  color: #fde68a;
}

.rotate-angle-hint {
  bottom: 122px;
  background: rgba(15, 23, 42, 0.92);
  border-color: #60a5fa;
  color: #bfdbfe;
  min-width: 150px;
  text-align: center;
  font-weight: 600;
}

.compass-indicator {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: var(--z-scene-compass);
  width: 56px;
  height: 56px;
  border-radius: 999px;
  border: 1px solid #3a5070;
  background: rgba(15, 25, 40, 0.84);
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.35);
  pointer-events: none;
}

.compass-ring {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.12s linear;
}

.compass-ring::before,
.compass-ring::after {
  content: '';
  position: absolute;
  background: rgba(99, 147, 196, 0.35);
}

.compass-ring::before {
  left: 50%;
  top: 6px;
  bottom: 6px;
  width: 1px;
  transform: translateX(-50%);
}

.compass-ring::after {
  top: 50%;
  left: 6px;
  right: 6px;
  height: 1px;
  transform: translateY(-50%);
}

.dir {
  position: absolute;
  font-size: 10px;
  font-weight: 700;
  color: #9fc7ef;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.45);
}

.dir.north {
  top: 5px;
  left: 50%;
  transform: translateX(-50%);
  color: #ff8f8f;
}

.dir.south {
  bottom: 5px;
  left: 50%;
  transform: translateX(-50%);
}

.dir.east {
  right: 7px;
  top: 50%;
  transform: translateY(-50%);
}

.dir.west {
  left: 7px;
  top: 50%;
  transform: translateY(-50%);
}

.precise-input-overlay {
  position: fixed;
  z-index: var(--z-sys-input);
  display: flex;
  align-items: center;
  gap: 4px;
  background: #1a2535;
  border: 1px solid #4a9eff;
  border-radius: 6px;
  padding: 6px 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}

.precise-label {
  font-size: 11px;
  color: #8899aa;
  white-space: nowrap;
}

.precise-input {
  width: 70px;
  background: #243040;
  border: 1px solid #3a4a60;
  color: #e0f0ff;
  border-radius: 4px;
  padding: 3px 6px;
  font-size: 12px;
  outline: none;
}

.precise-input:focus { border-color: #4a9eff; }

.precise-unit {
  font-size: 11px;
  color: #556677;
}

.precise-confirm {
  padding: 2px 8px;
  background: #1e4a8a;
  border: 1px solid #4a9eff;
  border-radius: 4px;
  color: #ddf;
  cursor: pointer;
  font-size: 12px;
}

.dev-test-panel {
  position: absolute;
  bottom: 8px;
  /* 与右侧对象列表/属性栏同宽 300px，留出间隙避免压住面板 */
  right: calc(300px + 10px);
  z-index: var(--z-sys-devtools);
  display: flex;
  gap: 4px;
}

.dev-btn {
  padding: 4px 8px;
  background: rgba(255, 200, 0, 0.85);
  border: 1px solid #aa8800;
  border-radius: 4px;
  color: #333;
  font-size: 11px;
  cursor: pointer;
}

.dev-fps {
  padding: 4px 6px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 4px;
  color: #00ff88;
  font-size: 11px;
  font-family: monospace;
}
</style>
